import styles from './styles.css';
import { TRANSLATIONS, localeKey, SPEECH_MAP } from './i18n.js';
import { bindTableUi, normalizeTableUi, renderTableUi } from './components/table-renderer.js';
import { bindCriteriaChipsUi, normalizeCriteriaChipsUi, renderCriteriaChipsUi } from './components/criteria-chips-renderer.js';
import { bindRadarExitAction, renderRadarExitAction } from './components/radar-exit-action-renderer.js';
import { bindCapabilityBlockViewUi, normalizeCapabilityBlockViewUi, renderCapabilityBlockViewUi } from './components/capability-block-view-renderer.js';
import { normalizeEmailBriefUi, renderEmailBriefUi, bindEmailBriefUi } from './components/email-brief-renderer.js';
import { normalizeEmailSenderUi, renderEmailSenderUi, bindEmailSenderUi } from './components/email-sender-renderer.js';
import { normalizeEmailGenerateUi, renderEmailGenerateUi, bindEmailGenerateUi } from './components/email-generate-renderer.js';
import { normalizeEmailAdaptiveEstimateUi, renderEmailAdaptiveEstimateUi, bindEmailAdaptiveEstimateUi } from './components/email-adaptive-estimate-renderer.js';
import { normalizeEmailPreviewUi, renderEmailPreviewUi, bindEmailPreviewUi } from './components/email-preview-renderer.js';
import { normalizeEmailCampaignTestUi, renderEmailCampaignTestUi, bindEmailCampaignTestUi } from './components/email-campaign-test-renderer.js';
import { normalizeEmailSendUi, renderEmailSendUi, bindEmailSendUi } from './components/email-send-renderer.js';
import { bindFieldSummaryUi, normalizeFieldSummaryUi, renderFieldSummaryUi } from './components/field-summary-renderer.js';
import { bindDictationActivityDraftUi, normalizeDictationActivityDraftUi, renderDictationActivityDraftUi } from './components/dictation-activity-draft-renderer.js';
import { bindDictationWritingPlanUi, normalizeDictationWritingPlanUi, renderDictationWritingPlanUi } from './components/dictation-writing-plan-renderer.js';
import { normalizeDictationWritingResultUi, renderDictationWritingResultUi } from './components/dictation-writing-result-renderer.js';
import { bindQuoteDraftUi, normalizeQuoteDraftUi, renderQuoteDraftUi } from './components/quote-draft-renderer.js';
import { bindClarificationOptionsUi, normalizeClarificationOptionsUi, renderClarificationOptionsUi } from './components/clarification-options-renderer.js';
import {
  bindDebriefAudioPopin,
  bindDebriefMatterIngestionUi,
  normalizeDebriefMatterIngestionUi,
  renderDebriefAudioPopin,
  renderDebriefMatterIngestionUi
} from './components/debrief-matter-ingestion-renderer.js';
import { bindEmailSimpleDraftUi, normalizeEmailSimpleDraftUi, renderEmailSimpleDraftUi } from './components/email-simple-draft-renderer.js';
import {
  applyCapabilityAutocompleteSelection,
  getCapabilityAutocompleteState,
  normalizeCapabilityAutocompleteItems,
  renderCapabilityAutocomplete
} from './components/capability-autocomplete.js';
import { renderHistoryConsole } from './components/history-renderer.js';
import { createTypingIndicator } from './components/typing-indicator.js';
import { renderHistoryPopin } from './components/history-popin.js';
import { renderAttentionPointsBody, renderAttentionPointsPopin, renderOperationalAgenda } from './components/attention-points-renderer.js';
import { buildAttentionPointHandoffAction } from './components/attention-points-handoff.js';
import { normalizeTokenQuotaState, renderTokenQuotaNotice } from './components/token-quota-renderer.js';
import { renderMessageText, renderUserMessageText } from './components/message-text-renderer.js';
import { normalizeCustomAgentResponseUi, renderCustomAgentResponseUi, bindCustomAgentResponseActions } from './components/custom-agent-response-renderer.js';
import { getAppVersion } from './app-version.js';
import { createElement, loadCss } from './utils/dom.js';
import { renderLucideIcon } from './utils/lucide-icons.js';
import { normalizeObjectKeys, toPositiveInteger, pickFirstDefined } from './utils/object.js';
import { normalizeMissionState, normalizeNewSkillState } from './adapters/mission-state.js';
import { installStreamMethods } from './widget-stream.js';
import { pickWelcomeGreeting } from './welcome-greetings.js';
import { getUserDisplayName } from './user-display-name.js';
import { getConversationContextLabels } from './components/conversation-context.js';
import { resolveRoutingChatReply } from './components/routing-response.js';
import { bindWelcomeActions, renderWelcomeActions } from './components/welcome-actions.js';
(function (global) {
  'use strict'

  function Widget(opts) {
    this.opts = opts || {}
    this.apiUrl = this.opts.apiUrl || ''
    this.authBaseUrl = this.opts.authBaseUrl || this.opts.apiBaseUrl || this._inferAuthBaseUrl(this.apiUrl)
    this.accessTokenKey = this.opts.accessTokenKey || 'angel_ia_access_token'
    this.refreshTokenKey = this.opts.refreshTokenKey || 'angel_ia_refresh_token'
    this.userKey = this.opts.userKey || 'angel_ia_user'
    this.config = normalizeObjectKeys(this.opts.config || {})
    this.requestContext = normalizeObjectKeys(this.opts.requestContext || this.config.requestContext || {})
    this.authToken = this.opts.authToken || this.config.authToken || ''
    this.refreshToken = this.opts.refreshToken || this.config.refreshToken || ''
    this.ssoJwt = this.opts.ssoJwt || this.config.ssoJwt || ''
    this.useCookieAuth = !!(this.opts.useCookieAuth || this.config.useCookieAuth)
    this.mode = this.opts.mode || 'floating'
    this.fullPage = !!(this.opts.fullPage || this.config.fullPage)
    this.conversationSidebarEnabled = this.opts.conversationSidebar !== false && this.config.conversationSidebar !== false
    this.stream = !!(this.opts.stream || this.config.stream)
    this.containerId = this.opts.containerId || null
    this.opened = false
    this.expanded = false
    this.recognition = null
    this._quoteDraftRecognition = null
    this._quoteDraftRecognizing = false
    this._quoteDraftLastDictation = ''
    this.recognizing = false
    // determine locale and use translations as defaults
    var chosenLangRaw = this.config.lang || this.opts.lang || (navigator.languages && navigator.languages[0]) || navigator.language || navigator.userLanguage || 'fr'
    var lk = localeKey(chosenLangRaw)
    this.lang = lk
    this.speechLang = SPEECH_MAP[lk] || 'fr-FR'
    var T = TRANSLATIONS[lk] || TRANSLATIONS.fr
    this.translations = T
    this.welcomeGreeting = pickWelcomeGreeting(lk)
    this.title = this.config.chatTitle || this.opts.title || T.chatTitle || 'Chat'
    this.assistantTitle = this.config.assistantTitle || this.config.botTitle || this.opts.assistantTitle || this.opts.botTitle || this.title
    this.placeholder = this.config.placeholder || this.opts.placeholder || T.placeholder
    this.sendLabel = this.config.sendLabel || this.opts.sendLabel || T.sendLabel
    this.ariaLabel = this.config.ariaLabel || this.opts.ariaLabel || T.ariaLabel
    this.tooltipText = this.config.tooltip || this.opts.tooltip || T.tooltip
    this.micLabel = this.config.micLabel || this.opts.micLabel || T.micLabel
    this.expandLabel = this.config.expandLabel || this.opts.expandLabel || T.expandLabel || 'Agrandir le panneau'
    this.collapseLabel = this.config.collapseLabel || this.opts.collapseLabel || T.collapseLabel || 'Réduire le panneau'
    this.closeLabel = this.config.closeLabel || this.opts.closeLabel || T.closeLabel || 'Fermer le panneau'
    this.adminLabel = this.config.adminLabel || this.opts.adminLabel || 'Accès admin'
    this.logoutLabel = this.config.logoutLabel || this.opts.logoutLabel || 'Déconnexion'
    this.version = this.config.version || this.opts.version || getAppVersion()
    this.adminUrl = this.config.adminUrl || this.opts.adminUrl || ''
    this.onAdminClick = typeof this.opts.onAdminClick === 'function' ? this.opts.onAdminClick : null
    this.onLogoutClick = typeof this.opts.onLogoutClick === 'function' ? this.opts.onLogoutClick : null
    this._capabilityCatalogItems = normalizeCapabilityAutocompleteItems(this.config.capabilities || this.opts.capabilities || [])
    this._capabilityCatalogLoading = false
    this._capabilityAutocompleteState = null
    this._capabilityAutocompleteActiveIndex = 0
    this._quoteDraftPopinEl = null
    this._debriefAudioPopinEl = null
    this._quoteDraftDialogue = []
    this._quoteDraftPending = false
    this._destroyed = false
    this._activeWelcomeAction = null

    // An SSO assertion identifies the current host account. Never let a
    // bearer session left by a previous account win over a fresh assertion.
    if (this.ssoJwt) {
      this._clearPersistedSession()
      this.authToken = ''
      this.refreshToken = ''
    }
    
    
    this.classMap = Object.assign({
      launcher: 'wc-launcher',
      panel: 'wc-panel',
      header: 'wc-header',
      title: 'wc-title',
      messages: 'wc-messages',
      footer: 'wc-footer',
      input: 'wc-input',
      send: 'wc-send',
      message: 'wc-message',
      bot: 'wc-bot',
      user: 'wc-user'
    }, this.opts.classes || {})

    var styleConfig = normalizeObjectKeys(this.opts.style || this.config.style || {})
    this.style = {
      fontFamily: styleConfig.fontFamily || this.opts.fontFamily || '',
      primaryColor: styleConfig.primaryColor || '',
      user: normalizeObjectKeys(styleConfig.user || {}),
      bot: normalizeObjectKeys(styleConfig.bot || {}),
      chat: normalizeObjectKeys(styleConfig.chat || {})
    }
    this.userColor = this.style.user.color || '#1F2446'
    this.userBg = this.style.user.backgroundColor || '#F6F7FA'
    this.userBorder = this.style.user.borderColor || ''
    this.botColor = this.style.bot.color || '#1A1F6B'
    this.botBg = this.style.bot.backgroundColor || 'transparent'
    this.chatColor = this.style.chat.color || '#1A1F6B'
    this.chatBg = this.style.chat.backgroundColor || '#FFFFFF'
    this.chatBorder = this.style.chat.borderColor || ''
    this.primaryColor = this.style.primaryColor || '#1A1F6B'

    // holds last finalized dictation text between events
    this._lastDictation = ''
    this.uiState = { popin: null, version: this.version }
    this.attentionPointsPopin = null
    this._conversationId = this.opts.conversationId || null
    this._conversationSidebarItems = []
    this._conversationSidebarLoading = false
    this._tokenQuotaLoading = false
    this._tokenQuotaState = normalizeTokenQuotaState(null)
    this.history = Array.isArray(this.opts.initialHistory)
      ? this.opts.initialHistory
      : [
            { from: 'bot', welcome: true, text: this.opts.initialBotMessage || T.initialBotMessage }
          ]

    this._hydrateAuthState()
    this._authReady = this._bootstrapAuth()

    this._build()
  }

  Widget.prototype._cls = function (name) {
    return this.classMap[name] || ''
  }

  Widget.prototype._build = function () {
    loadCss(this.opts.css || styles)

    // launcher button
    if (this.mode === 'floating') {
      this.button = createElement('button', { class: this._cls('launcher'), 'aria-label': this.ariaLabel, 'aria-expanded': 'false' })
      this.button.innerHTML = renderLucideIcon('sparkles')
      this.tooltip = createElement('span', { class: 'wc-launcher-tooltip' }, [this.tooltipText])
      this.button.appendChild(this.tooltip)
      this.button.addEventListener('click', this.toggle.bind(this))
    }

    // panel
    this.panel = createElement('div', {
      class:
        this._cls('panel') +
        (this.mode === 'embedded' ? ' wc-embedded' : '') +
        (this.mode === 'embedded' && this.fullPage ? ' wc-full-page' : '') +
        (this.conversationSidebarEnabled ? ' wc-with-sidebar' : ''),
      role: 'dialog',
      'aria-hidden': this.mode === 'floating' ? 'true' : 'false'
    })
    var conversationSidebar = createElement('aside', {
      class: 'wc-conversation-sidebar',
      'aria-label': 'Conversations'
    })
    var sidebarBrand = createElement('div', { class: 'wc-conversation-sidebar-brand' })
    var sidebarMark = createElement('span', { class: 'wc-conversation-sidebar-mark' })
    sidebarMark.innerHTML = renderLucideIcon('sparkles')
    sidebarBrand.appendChild(sidebarMark)
    sidebarBrand.appendChild(createElement('strong', {}, [this.title]))
    this.conversationSidebarListEl = createElement('div', {
      class: 'wc-conversation-sidebar-list',
      'aria-live': 'polite'
    })
    conversationSidebar.appendChild(sidebarBrand)
    conversationSidebar.appendChild(this.conversationSidebarListEl)
    this.conversationSidebarEl = conversationSidebar

    var main = createElement('div', { class: 'wc-main' })
    var headerTitle = createElement('div', { class: 'wc-header-title' })
    var titleMark = createElement('span', { class: 'wc-title-mark' })
    titleMark.innerHTML = renderLucideIcon('sparkles')
    headerTitle.appendChild(titleMark)
    var subtitle = this.opts.subtitle || this.config.subtitle || ''
    var headerCopyChildren = [createElement('span', { class: this._cls('title') }, [this.title])]
    if (subtitle) {
      headerCopyChildren.push(createElement('span', { class: 'wc-title-subtitle' }, [subtitle]))
    }
    headerTitle.appendChild(createElement('span', { class: 'wc-header-copy' }, headerCopyChildren))
    var headerChildren = [headerTitle]
    this.attentionPointsButton = createElement('button', {
      class: 'wc-panel-action wc-panel-attention',
      type: 'button',
      'aria-label': 'Points d’attention',
      title: 'Points d’attention'
    })
    this.attentionPointsButton.innerHTML = renderLucideIcon('bell')
    this.attentionPointsButton.appendChild(createElement('span', {
      class: 'wc-attention-badge',
      'aria-hidden': 'true',
      hidden: 'hidden'
    }))
    this.attentionPointsButton.addEventListener('click', this._openAttentionPoints.bind(this))
    var actionButtons = [this.attentionPointsButton]
    if (this.mode === 'floating') {
      this.expandButton = createElement('button', {
        class: 'wc-panel-action wc-panel-expand',
        type: 'button',
        'aria-label': this.expandLabel,
        title: this.expandLabel
      })
      this.expandButton.innerHTML = renderLucideIcon('maximize')
      this.closeButton = createElement('button', {
        class: 'wc-panel-action wc-panel-close',
        type: 'button',
        'aria-label': this.closeLabel,
        title: this.closeLabel
      })
      this.closeButton.innerHTML = renderLucideIcon('x')
      this.expandButton.addEventListener('click', this.toggleExpanded.bind(this))
      this.closeButton.addEventListener('click', this.close.bind(this))
      actionButtons.push(this.expandButton, this.closeButton)
      headerChildren.push(createElement('div', { class: 'wc-panel-actions' }, actionButtons))
    } else if (this.adminUrl || this.onAdminClick || this.onLogoutClick) {
      var embeddedActions = createElement('div', { class: 'wc-panel-actions' })
      this.adminButton = this.adminUrl ? createElement('a', {
        class: 'wc-panel-action wc-panel-admin',
        href: this.adminUrl,
        'aria-label': this.adminLabel,
        title: this.adminLabel
      }) : createElement('button', {
        class: 'wc-panel-action wc-panel-admin',
        type: 'button',
        'aria-label': this.adminLabel,
        title: this.adminLabel
      })
      this.adminButton.innerHTML = renderLucideIcon('settings')
      this.logoutButton = createElement('button', {
        class: 'wc-panel-action wc-panel-logout',
        type: 'button',
        'aria-label': this.logoutLabel,
        title: this.logoutLabel
      })
      this.logoutButton.innerHTML = renderLucideIcon('log-out')
      if (this.onAdminClick) {
        this.adminButton.addEventListener('click', function (event) {
          this.onAdminClick(event)
        }.bind(this))
      }
      if (this.onLogoutClick) {
        this.logoutButton.addEventListener('click', function (event) {
          this.onLogoutClick(event)
        }.bind(this))
      }
      embeddedActions.appendChild(this.adminButton)
      embeddedActions.appendChild(this.logoutButton)
      embeddedActions.insertBefore(this.attentionPointsButton, embeddedActions.firstChild)
      headerChildren.push(embeddedActions)
    } else {
      headerChildren.push(createElement('div', { class: 'wc-panel-actions' }, actionButtons))
    }
    var header = createElement('div', { class: this._cls('header') }, headerChildren)
    var messages = createElement('div', { class: this._cls('messages') })
    var footer = createElement('div', { class: this._cls('footer') })
    var inputWrap = createElement('div', { class: 'wc-input-wrapper' })
    this.inputPromptEl = createElement('div', { class: 'wc-input-prompt', hidden: 'hidden' })
    this.capabilityAutocompleteEl = createElement('div', {
      class: 'wc-capability-autocomplete',
      id: 'wc-capability-autocomplete-' + Math.random().toString(36).slice(2),
      role: 'listbox',
      hidden: 'hidden'
    })
    this.input = createElement('textarea', {
      class: this._cls('input'),
      placeholder: this.placeholder,
      rows: '1',
      autocomplete: 'off',
      'aria-autocomplete': 'list',
      'aria-controls': this.capabilityAutocompleteEl.getAttribute('id')
    })
    this.micButton = createElement('button', { class: 'wc-mic', type: 'button', 'aria-label': this.micLabel, 'aria-pressed': 'false' })
    this.micButton.innerHTML = renderLucideIcon('mic')
    this.input.addEventListener('keydown', function (e) {
      if (this._handleCapabilityAutocompleteKeydown(e)) return
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        this._send()
      }
    }.bind(this))
    this.input.addEventListener('input', this._updateCapabilityAutocomplete.bind(this))
    this.input.addEventListener('click', this._updateCapabilityAutocomplete.bind(this))
    this.input.addEventListener('focus', this._updateCapabilityAutocomplete.bind(this))
    this.input.addEventListener('blur', function () {
      setTimeout(function () {
        this._hideCapabilityAutocomplete()
      }.bind(this), 120)
    }.bind(this))
    this.micButton.addEventListener('click', this._toggleDictation.bind(this))
    this.tokenQuotaNotice = createElement('div', { class: 'wc-token-quota-host' })
    inputWrap.appendChild(this.capabilityAutocompleteEl)
    inputWrap.appendChild(this.inputPromptEl)
    inputWrap.appendChild(this.input)
    this.sendButton = createElement('button', {
      class: this._cls('send'),
      type: 'button',
      'aria-label': this.sendLabel,
      title: this.sendLabel
    })
    this.sendButton.innerHTML = renderLucideIcon('send')
    this.sendButton.addEventListener('click', this._send.bind(this))
    footer.appendChild(this.tokenQuotaNotice)
    footer.appendChild(inputWrap)
    footer.appendChild(this.micButton)
    footer.appendChild(this.sendButton)

    main.appendChild(header)
    main.appendChild(messages)
    main.appendChild(footer)
    this.panel.appendChild(conversationSidebar)
    this.panel.appendChild(main)
    this._bindSupportShortcut()

    if (this.mode === 'floating') {
      document.body.appendChild(this.button);
      document.body.appendChild(this.panel);
    }
    else {
      var container =
          document.getElementById(this.containerId);

      if (!container)
          throw new Error(
              'Container "' + this.containerId + '" introuvable'
          );

      container.appendChild(this.panel);

      this.opened = true;
      this.panel.classList.add('wc-open');
    }

    this.messagesEl = messages
    this.footerEl = footer
    this._applyStyle(header)
    this._renderHistory()
    this._maybeLoadConversationSidebar()
    this._maybeLoadCapabilityCatalog()
    this._refreshTokenQuota()
    this._refreshAttentionBadge()
  }

  Widget.prototype._openAttentionPoints = function () {
    var root = this._ensureAttentionPointsPopin()
    root.classList.add('is-open')
    this._loadAttentionPoints(root)
  }

  Widget.prototype._loadAttentionPoints = function (root) {
    root.querySelector('.wc-attention-points-body').innerHTML = '<div class="wc-attention-empty">Chargement des points d’attention…</div>'
    if (!this.apiUrl) {
      this._updateAttentionBadge(0)
      return
    }
    this._sendRequest('/alerts', null, { method: 'GET' }).then(function (response) {
      this._attentionPointsResponse = response || {}
      var alerts = response && (response.alerts || response.items)
      this._attentionPointsItems = Array.isArray(alerts) ? alerts : []
      var alertCount = Array.isArray(alerts) ? alerts.length : 0
      this._updateAttentionBadge(alertCount)
      var tabCount = root.querySelector('.wc-attention-tab-count')
      if (tabCount) tabCount.textContent = String(alertCount)
      var alertsHtml = renderAttentionPointsBody(alerts)
      var agendaHtml = renderOperationalAgenda(response)
      var hasAlerts = response && Array.isArray(response.alerts || response.items) && (response.alerts || response.items).length
      var hasAgenda = agendaHtml.length > 0
      root.querySelector('.wc-attention-points-body').innerHTML = hasAlerts || hasAgenda
        ? alertsHtml + agendaHtml
        : '<div class="wc-attention-empty"><strong>Aucun point d’attention</strong><span>Aucune alerte ni activité opérationnelle ne vous concerne actuellement.</span></div>'
    }.bind(this)).catch(function () {
      this._updateAttentionBadge(0)
      root.querySelector('.wc-attention-points-body').innerHTML = '<div class="wc-attention-empty">Impossible de charger les points d’attention.</div>'
    })
  }

  Widget.prototype._refreshAttentionBadge = function () {
    if (!this.apiUrl) return
    this._sendRequest('/alerts', null, { method: 'GET' }).then(function (response) {
      var alerts = response && (response.alerts || response.items)
      this._updateAttentionBadge(Array.isArray(alerts) ? alerts.length : 0)
    }.bind(this)).catch(function () {
      this._updateAttentionBadge(0)
    }.bind(this))
  }

  Widget.prototype._updateAttentionBadge = function (count) {
    if (!this.attentionPointsButton) return
    var badge = this.attentionPointsButton.querySelector('.wc-attention-badge')
    if (!badge) return
    var value = Number.isFinite(Number(count)) ? Math.max(0, Number(count)) : 0
    badge.textContent = String(value)
    badge.hidden = value === 0
    this.attentionPointsButton.setAttribute('aria-label', value
      ? 'Points d’attention, ' + value + ' alerte' + (value > 1 ? 's' : '')
      : 'Points d’attention')
  }

  Widget.prototype._ensureAttentionPointsPopin = function () {
    if (this.attentionPointsPopin) return this.attentionPointsPopin
    var holder = createElement('div')
    holder.innerHTML = renderAttentionPointsPopin({ loading: true })
    this.attentionPointsPopin = holder.firstElementChild
    this.attentionPointsPopin.addEventListener('click', function (event) {
      if (event.target.closest('[data-popin-close]')) this.attentionPointsPopin.classList.remove('is-open')
      var snoozeToggle = event.target.closest('[data-alert-snooze-toggle]')
      if (snoozeToggle) {
        var menu = snoozeToggle.parentElement.querySelector('[data-alert-snooze-menu]')
        var wasHidden = !menu || menu.hidden
        this.attentionPointsPopin.querySelectorAll('[data-alert-snooze-menu]').forEach(function (element) { element.hidden = true })
        this.attentionPointsPopin.querySelectorAll('[data-alert-snooze-toggle]').forEach(function (element) { element.setAttribute('aria-expanded', 'false') })
        if (menu) {
          menu.hidden = !wasHidden
          snoozeToggle.setAttribute('aria-expanded', String(wasHidden))
        }
        return
      }
      var decision = event.target.closest('[data-alert-decision]')
      if (decision) {
        var alertId = decision.getAttribute('data-alert-id')
        var status = decision.getAttribute('data-alert-decision')
        var days = Number(decision.getAttribute('data-snooze-days') || 0)
        var body = { status: status }
        if (status === 'snoozed') body.snoozedUntil = new Date(Date.now() + days * 86400000).toISOString()
        rootDecisionLoading(decision)
        this._sendRequest('/alerts/' + encodeURIComponent(alertId) + '/status', body, { method: 'PATCH' })
          .then(function () { this._loadAttentionPoints(this.attentionPointsPopin) }.bind(this))
          .catch(function () { decision.disabled = false; decision.textContent = 'Réessayer' })
        this.attentionPointsPopin.querySelectorAll('[data-alert-snooze-menu]').forEach(function (element) { element.hidden = true })
        this.attentionPointsPopin.querySelectorAll('[data-alert-snooze-toggle]').forEach(function (element) { element.setAttribute('aria-expanded', 'false') })
        return
      }
      var action = event.target.closest('[data-alert-action-index]')
      if (action) {
        this._handleAttentionAction(action)
        return
      }
      var briefing = event.target.closest('[data-activity-briefing]')
      if (briefing) {
        var activityId = briefing.getAttribute('data-activity-briefing')
        var activity = findActivity(this._attentionPointsResponse, activityId)
        if (activity) {
          this.attentionPointsPopin.classList.remove('is-open')
          this._sendCapabilityAction({
            label: 'Préparer avec @Briefing',
            targetCapability: 'BRIEFING',
            displayLabel: '@Briefing',
            requestContext: {
              currentRecord: { objectType: 'activity', recordId: activity.id, label: activity.title || null },
              capabilitySuggestion: { originCapability: 'ALERTS', targetCapability: 'BRIEFING', label: 'Préparer avec @Briefing', originContext: { activity } }
            }
          }, { capability: 'ALERTS' })
        }
        return
      }
      var explanationToggle = event.target.closest('[data-attention-explanation]')
      if (explanationToggle) {
        var explanation = explanationToggle.nextElementSibling
        var expanded = explanationToggle.getAttribute('aria-expanded') === 'true'
        explanationToggle.setAttribute('aria-expanded', expanded ? 'false' : 'true')
        if (explanation) explanation.hidden = expanded
        explanationToggle.textContent = expanded ? 'Voir pourquoi' : 'Masquer pourquoi'
      }
      if (!event.target.closest('.wc-attention-dropdown')) {
        this.attentionPointsPopin.querySelectorAll('[data-alert-snooze-menu]').forEach(function (element) { element.hidden = true })
        this.attentionPointsPopin.querySelectorAll('[data-alert-snooze-toggle]').forEach(function (element) { element.setAttribute('aria-expanded', 'false') })
      }
    }.bind(this))
    this.panel.appendChild(this.attentionPointsPopin)
    return this.attentionPointsPopin
  }

  function findActivity(response, activityId) {
    var groups = response && response.activities ? response.activities : {}
    return ['overdue', 'today', 'upcoming'].reduce(function (found, key) {
      return found || (Array.isArray(groups[key]) ? groups[key].find(function (item) { return String(item && item.id) === String(activityId) }) : null)
    }, null)
  }

  Widget.prototype._handleAttentionAction = function (button) {
    var alertId = button.getAttribute('data-alert-id')
    var actionIndex = Number(button.getAttribute('data-alert-action-index'))
    var alert = (this._attentionPointsItems || []).find(function (item) { return String(item && item.id) === String(alertId) })
    var action = alert && Array.isArray(alert.suggestedActions) ? alert.suggestedActions[actionIndex] : null
    var handoffAction = buildAttentionPointHandoffAction(alert, action)
    if (!handoffAction) return

    this.attentionPointsPopin.classList.remove('is-open')
    this._sendCapabilityAction(handoffAction, { capability: 'ALERTS' })
  }

  function rootDecisionLoading(button) {
    button.disabled = true
    button.textContent = 'Enregistrement…'
  }

  Widget.prototype.toggle = function () {
    this.opened ? this.close() : this.open()
  }

  Widget.prototype.open = function () {
    if (this.mode === 'embedded')
      return;
    this.panel.classList.add('wc-open');
    this.panel.setAttribute('aria-hidden', 'false');
    if (this.button) {
      this.button.classList.add('wc-launcher-hidden')
      this.button.setAttribute('aria-expanded', 'true')
    }
    this.opened = true;
    this.input.focus();
  }

  Widget.prototype.toggleExpanded = function () {
    if (this.mode === 'embedded')
      return
    this.expanded = !this.expanded
    this.panel.classList.toggle('wc-expanded', this.expanded)
    if (this.expandButton) {
      var label = this.expanded ? this.collapseLabel : this.expandLabel
      this.expandButton.setAttribute('aria-label', label)
      this.expandButton.setAttribute('title', label)
      this.expandButton.classList.toggle('wc-panel-collapse', this.expanded)
    }
    this._maybeLoadConversationSidebar()
  }

  Widget.prototype._toggleDictation = function () {
    if (this._isTokenQuotaBlocked()) {
      this._applyTokenQuotaState(this._tokenQuotaState)
      return
    }
    if (!window.SpeechRecognition && !window.webkitSpeechRecognition) {
      return
    }
    if (!this.recognition) {
      var SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
      this.recognition = new SpeechRecognition()
      // use resolved speech language
      this.recognition.lang = this.speechLang
      this.recognition.interimResults = true
      this.recognition.continuous = false
      this.recognition.onresult = function (event) {
        var interim = ''
        var finalTranscript = ''
        for (var i = event.resultIndex; i < event.results.length; i++) {
          var r = event.results[i]
          if (r.isFinal) finalTranscript += r[0].transcript
          else interim += r[0].transcript
        }
        // show interim + final in the input
        this.input.value = (this._lastDictation || '') + interim + finalTranscript
        // remember the finalized part so we can send on end
        if (finalTranscript) this._lastDictation = (this._lastDictation || '') + finalTranscript
      }.bind(this)
      this.recognition.onend = function () {
        this.recognizing = false
        this.micButton.classList.remove('wc-mic-active')
        this.micButton.setAttribute('aria-pressed', 'false')
        if (this.input) this.input.focus()
        // if we have any dictated text, mimic pressing Enter (send)
        var txt = (this.input && this.input.value) ? this.input.value.trim() : ''
        if (txt) {
          // small timeout to allow UI updates before sending
          setTimeout(function () { this._send() }.bind(this), 500)
        }
        // reset last dictation buffer
        this._lastDictation = ''
      }.bind(this)
    }
    if (this.recognizing) {
      this.recognition.stop()
      return
    }
    this.recognizing = true
    this.micButton.classList.add('wc-mic-active')
    this.micButton.setAttribute('aria-pressed', 'true')
    this.recognition.start()
    this.input.focus()
  }

  Widget.prototype._maybeLoadCapabilityCatalog = function () {
    if (this._capabilityCatalogLoading || this._capabilityCatalogItems.length || !this.apiUrl) return
    this._capabilityCatalogLoading = true

    Promise.resolve(this._authReady).then(function () {
      return this._sendRequest('/capabilities', null, { method: 'GET' })
    }.bind(this)).then(function (response) {
      this._capabilityCatalogItems = normalizeCapabilityAutocompleteItems(response)
      this._capabilityCatalogLoading = false
      if (this.messagesEl) {
        Array.prototype.forEach.call(this.messagesEl.children, function (message) {
          if (typeof message._userMessageText !== 'string') return
          var body = message.querySelector('.wc-message-body')
          if (body) body.innerHTML = renderUserMessageText(message._userMessageText, this._capabilityCatalogItems)
        }, this)
      }
      this._updateCapabilityAutocomplete()
    }.bind(this)).catch(function () {
      this._capabilityCatalogLoading = false
    }.bind(this))
  }

  Widget.prototype._refreshTokenQuota = function () {
    if (this._tokenQuotaLoading || !this.apiUrl) return Promise.resolve(this._tokenQuotaState)
    this._tokenQuotaLoading = true

    return Promise.resolve(this._authReady).then(function () {
      return this._sendRequest('/token-balance', null, { method: 'GET' })
    }.bind(this)).then(function (response) {
      this._tokenQuotaLoading = false
      return this._applyTokenQuotaState(response)
    }.bind(this)).catch(function () {
      this._tokenQuotaLoading = false
      return this._tokenQuotaState
    }.bind(this))
  }

  Widget.prototype._applyTokenQuotaState = function (state) {
    this._tokenQuotaState = normalizeTokenQuotaState(state)
    var blocked = this._tokenQuotaState.blocked

    if (this.input) {
      this.input.disabled = blocked
      this.input.setAttribute('aria-disabled', blocked ? 'true' : 'false')
      if (blocked) {
        this.input.setAttribute('placeholder', this._resolveTokenQuotaPlaceholder())
      } else {
        this.input.setAttribute('placeholder', this.placeholder)
      }
    }
    if (this.sendButton) {
      this.sendButton.disabled = blocked
      this.sendButton.setAttribute('aria-disabled', blocked ? 'true' : 'false')
    }
    if (this.micButton) {
      this.micButton.disabled = blocked
      this.micButton.setAttribute('aria-disabled', blocked ? 'true' : 'false')
    }
    if (this.footerEl) {
      this.footerEl.classList.toggle('wc-token-quota-blocked', blocked)
    }
    if (this.tokenQuotaNotice) {
      this.tokenQuotaNotice.innerHTML = renderTokenQuotaNotice(
        this._tokenQuotaState,
        this.translations && this.translations.tokenQuota
      )
      this.tokenQuotaNotice.hidden = !blocked
    }

    return this._tokenQuotaState
  }

  Widget.prototype._isTokenQuotaBlocked = function () {
    return Boolean(this._tokenQuotaState && this._tokenQuotaState.blocked)
  }

  Widget.prototype._resolveTokenQuotaPlaceholder = function () {
    return (this.translations && this.translations.tokenQuota && this.translations.tokenQuota.placeholder) ||
      'Saisie désactivée'
  }

  Widget.prototype._updateCapabilityAutocomplete = function () {
    if (!this.input || !this.capabilityAutocompleteEl || !this._capabilityCatalogItems.length) {
      this._hideCapabilityAutocomplete()
      return
    }

    var caret = typeof this.input.selectionStart === 'number' ? this.input.selectionStart : this.input.value.length
    var state = getCapabilityAutocompleteState(this.input.value, caret, this._capabilityCatalogItems)
    if (!state.open) {
      this._hideCapabilityAutocomplete()
      return
    }

    this._capabilityAutocompleteState = state
    this._capabilityAutocompleteActiveIndex = Math.min(
      Math.max(0, this._capabilityAutocompleteActiveIndex || 0),
      Math.max(0, state.suggestions.length - 1)
    )
    this.capabilityAutocompleteEl.innerHTML = renderCapabilityAutocomplete(
      state.suggestions,
      this._capabilityAutocompleteActiveIndex,
      this.translations.capabilityAutocomplete
    )
    this.capabilityAutocompleteEl.removeAttribute('hidden')
    this.input.setAttribute('aria-expanded', 'true')
    this._bindCapabilityAutocompleteActions()
  }

  Widget.prototype._bindCapabilityAutocompleteActions = function () {
    if (!this.capabilityAutocompleteEl) return
    Array.prototype.slice.call(this.capabilityAutocompleteEl.querySelectorAll('[data-capability-suggestion-index]')).forEach(function (button) {
      button.addEventListener('mousedown', function (event) {
        event.preventDefault()
      })
      button.addEventListener('click', function () {
        var index = Number(button.getAttribute('data-capability-suggestion-index'))
        this._selectCapabilityAutocompleteSuggestion(index)
      }.bind(this))
    }.bind(this))
  }

  Widget.prototype._handleCapabilityAutocompleteKeydown = function (event) {
    if (!this.capabilityAutocompleteEl || this.capabilityAutocompleteEl.hasAttribute('hidden')) return false
    var state = this._capabilityAutocompleteState
    var count = state && Array.isArray(state.suggestions) ? state.suggestions.length : 0

    if (event.key === 'Escape') {
      event.preventDefault()
      this._hideCapabilityAutocomplete()
      return true
    }

    if (event.key === 'ArrowDown' && count) {
      event.preventDefault()
      this._capabilityAutocompleteActiveIndex = (this._capabilityAutocompleteActiveIndex + 1) % count
      this._updateCapabilityAutocomplete()
      return true
    }

    if (event.key === 'ArrowUp' && count) {
      event.preventDefault()
      this._capabilityAutocompleteActiveIndex = (this._capabilityAutocompleteActiveIndex - 1 + count) % count
      this._updateCapabilityAutocomplete()
      return true
    }

    if ((event.key === 'Enter' || event.key === 'Tab') && count) {
      event.preventDefault()
      this._selectCapabilityAutocompleteSuggestion(this._capabilityAutocompleteActiveIndex)
      return true
    }

    return false
  }

  Widget.prototype._selectCapabilityAutocompleteSuggestion = function (index) {
    var state = this._capabilityAutocompleteState
    var suggestions = state && Array.isArray(state.suggestions) ? state.suggestions : []
    var item = suggestions[index]
    if (!item || item.available === false) return

    this.input.value = applyCapabilityAutocompleteSelection(this.input.value, state.trigger, item)
    var caret = state.trigger.start + state.trigger.marker.length + item.shortcut.length + 1
    if (typeof this.input.setSelectionRange === 'function') {
      this.input.setSelectionRange(caret, caret)
    }
    this._hideCapabilityAutocomplete()
    this.input.focus()
  }

  Widget.prototype._hideCapabilityAutocomplete = function () {
    this._capabilityAutocompleteState = null
    this._capabilityAutocompleteActiveIndex = 0
    if (this.capabilityAutocompleteEl) {
      this.capabilityAutocompleteEl.innerHTML = ''
      this.capabilityAutocompleteEl.setAttribute('hidden', 'hidden')
    }
    if (this.input) this.input.setAttribute('aria-expanded', 'false')
  }

  Widget.prototype.close = function () {
    if (this.mode === 'embedded')
      return;
    this.panel.classList.remove('wc-open')
    this.panel.setAttribute('aria-hidden', 'true')
    if (this.button) {
      this.button.classList.remove('wc-launcher-hidden')
      this.button.setAttribute('aria-expanded', 'false')
    }
    this.opened = false
  }

  Widget.prototype._applyStyle = function (header) {
    // Apply only CSS variables from configuration. All visual styles live in styles.css.
    var el = this.panel
    if (this.style.fontFamily) el.style.setProperty('--wc-font-family', this.style.fontFamily)
    if (this.primaryColor) {
      el.style.setProperty('--wc-primary-color', this.primaryColor)
      if (this.button)
        this.button.style.setProperty('--wc-primary-color', this.primaryColor)
      if (this.panel)
        this.panel.style.setProperty('--wc-primary-color', this.primaryColor)
    }
    if (this.chatBg) el.style.setProperty('--wc-chat-bg', this.chatBg)
    if (this.chatColor) el.style.setProperty('--wc-chat-color', this.chatColor)
    if (this.userColor) el.style.setProperty('--wc-user-color', this.userColor)
    if (this.userBg) el.style.setProperty('--wc-user-bg', this.userBg)
    if (this.botColor) el.style.setProperty('--wc-bot-color', this.botColor)
    if (this.botBg) el.style.setProperty('--wc-bot-bg', this.botBg)
    if (this.userBorder) el.style.setProperty('--wc-user-border-color', this.userBorder)
    if (this.chatBorder) el.style.setProperty('--wc-chat-border-color', this.chatBorder)
  }

  Widget.prototype._append = function (text, from, meta) {
    var classes = this._cls('message') + ' ' + (from === 'user' ? this._cls('user') : this._cls('bot'))
    var styles = {}
    if (from === 'user') {
      styles.color = this.userColor
      styles.backgroundColor = this.userBg
    } else {
      styles.color = this.botColor
      styles.backgroundColor = this.botBg
    }
    var m = createElement('div', { class: classes })
    if (from === 'user') m._userMessageText = String(text || '')
    m.innerHTML = from === 'user' ? renderUserMessageText(text, this._capabilityCatalogItems) : renderMessageText(text)
    this._decorateChatMessage(m, from, meta || { copyText: text })
    if (styles.color) m.style.setProperty('--wc-msg-color', styles.color)
    if (styles.backgroundColor) m.style.setProperty('--wc-msg-bg', styles.backgroundColor)
    this.messagesEl.appendChild(m)
    this._scrollToBottom()
    return m
  }

  Widget.prototype._decorateChatMessage = function (el, from, meta) {
    if (!el || !el.classList || el.getAttribute('data-wc-message-decorated') === 'true') return
    if (el.classList.contains('wc-typing')) return
    meta = meta || {}
    if (from !== 'user' && !meta.eventId && this._activeAssistantEventId) {
      meta.eventId = this._activeAssistantEventId
    }
    var body = createElement('div', { class: 'wc-message-body' })
    while (el.firstChild) {
      body.appendChild(el.firstChild)
    }

    var header = from === 'user'
      ? this._createUserMessageHeader()
      : this._createAssistantMessageHeader()

    el.appendChild(header)
    el.appendChild(body)
    if (from !== 'user' && !meta.suppressActions) {
      el.appendChild(this._createAssistantMessageActions(meta))
    }
    if (meta.suppressActions) {
      el.classList.add('wc-intro-message')
    }
    el.setAttribute('data-wc-message-decorated', 'true')
  }

  Widget.prototype._appendWelcome = function () {
    var classes = this._cls('message') + ' ' + this._cls('bot') + ' wc-welcome-message'
    var el = createElement('div', { class: classes })
    var greeting = this.welcomeGreeting || pickWelcomeGreeting(this.lang)
    el.innerHTML =
      '<div class="wc-welcome-title">' + escapeHtml(greeting.text) + ' <span aria-hidden="true">' + escapeHtml(greeting.emoji) + '</span></div>' +
      '<p class="wc-welcome-copy">Je suis <strong>' + escapeHtml(this.assistantTitle) + '</strong>. Posez-moi vos questions, demandez une analyse ou confiez-moi une tâche. Je peux vous aider à travailler plus vite.</p>'
      + renderWelcomeActions(this.translations && this.translations.welcomeActions)
    bindWelcomeActions(el, this.translations && this.translations.welcomeActions, {
      onSelect: function (action, button) {
        this._prepareWelcomeAction(action, button)
      }.bind(this)
    })
    this.messagesEl.appendChild(el)
    this._scrollToBottom()
  }

  Widget.prototype._prepareWelcomeAction = function (action, button) {
    if (!action || !this.input) return
    if (this._isTokenQuotaBlocked()) {
      this._applyTokenQuotaState(this._tokenQuotaState)
      return
    }

    this._hideCapabilityAutocomplete()
    this._activeWelcomeAction = action
    this.input.value = action.shortcut + ' '
    this.inputPromptEl.textContent = action.prompt
    this.inputPromptEl.removeAttribute('hidden')
    this.input.setAttribute('aria-label', action.prompt)
    if (button && button.parentNode) {
      Array.prototype.slice.call(button.parentNode.querySelectorAll('.wc-welcome-action')).forEach(function (item) {
        item.classList.toggle('is-selected', item === button)
      })
    }
    this.input.focus()
    if (typeof this.input.setSelectionRange === 'function') {
      var caret = this.input.value.length
      this.input.setSelectionRange(caret, caret)
    }
  }

  Widget.prototype._clearWelcomeAction = function () {
    this._activeWelcomeAction = null
    if (this.inputPromptEl) {
      this.inputPromptEl.textContent = ''
      this.inputPromptEl.setAttribute('hidden', 'hidden')
    }
    if (this.input) this.input.setAttribute('aria-label', this.placeholder)
    if (this.messagesEl) {
      Array.prototype.slice.call(this.messagesEl.querySelectorAll('.wc-welcome-action.is-selected')).forEach(function (button) {
        button.classList.remove('is-selected')
      })
    }
  }

  Widget.prototype._createUserMessageHeader = function () {
    return createElement('div', { class: 'wc-message-header wc-message-header-user' }, [
      createElement('strong', { class: 'wc-message-author' }, [this._getUserDisplayName()])
    ])
  }

  Widget.prototype._createAssistantMessageHeader = function () {
    var icon = createElement('span', { class: 'wc-message-assistant-icon' })
    icon.innerHTML = renderLucideIcon('sparkles')
    return createElement('div', { class: 'wc-message-header wc-message-header-assistant' }, [
      icon,
      createElement('strong', { class: 'wc-message-author' }, [this.assistantTitle])
    ])
  }

  Widget.prototype._createAssistantMessageActions = function (meta) {
    var actions = createElement('div', { class: 'wc-message-actions' })
    actions.appendChild(this._createMessageActionButton('copy', 'Copier le résultat', 'copy', meta))
    actions.appendChild(this._createMessageActionButton('feedback-ok', 'Noter positivement', 'thumbsUp', meta))
    actions.appendChild(this._createMessageActionButton('feedback-not-ok', 'Noter négativement', 'thumbsDown', meta))
    return actions
  }

  Widget.prototype._createMessageActionButton = function (action, label, iconName, meta) {
    var button = createElement('button', {
      class: 'wc-message-action',
      type: 'button',
      'aria-label': label,
      title: label,
      'data-wc-message-action': action
    })
    if (meta && meta.eventId) {
      button.setAttribute('data-conversation-event-id', String(meta.eventId))
    }
    if (meta && meta.radarRunId) {
      button.setAttribute('data-radar-run-id', String(meta.radarRunId))
    }
    button.innerHTML = renderLucideIcon(iconName)
    button.addEventListener('click', function () {
      this._handleMessageAction(button, meta || {})
    }.bind(this))
    return button
  }

  Widget.prototype._handleMessageAction = function (button, meta) {
    var action = button.getAttribute('data-wc-message-action')
    var message = button.closest ? button.closest('.wc-message') : null
    if (action === 'copy') {
      this._copyMessageResult(message, button, meta)
      return
    }

    var rating = action === 'feedback-ok'
      ? 'ok'
      : action === 'feedback-not-ok'
        ? 'not_ok'
        : ''
    if (!rating) return

    var radarRunId = button.getAttribute('data-radar-run-id') || findNestedRadarRunId(message)
    if (radarRunId) {
      var radarScope = createElement('span', { 'data-radar-run-id': radarRunId })
      var radarButton = createElement('button', { 'data-radar-feedback-rating': rating })
      radarScope.appendChild(radarButton)
      this._handleRadarFeedback(radarButton)
      this._setMessageFeedbackRating(message, rating)
      return
    }

    var eventId = button.getAttribute('data-conversation-event-id') || (meta && meta.eventId)
    if (eventId && this._conversationId) {
      this._handleConversationEventFeedback(message, eventId, rating)
      return
    }
    this._setMessageFeedbackRating(message, rating)
  }

  Widget.prototype._handleConversationEventFeedback = function (message, eventId, rating) {
    if (!message || !eventId || !this._conversationId) return
    this._setMessageActionButtonsDisabled(message, true)
    message.setAttribute('data-feedback-state', 'loading')
    this._sendRequest(
      '/gateway/conversations/' + encodeURIComponent(this._conversationId) + '/events/' + encodeURIComponent(eventId) + '/feedback',
      { rating: rating },
      { method: 'PATCH' }
    ).then(function () {
      message.setAttribute('data-feedback-state', 'sent')
      this._setMessageFeedbackRating(message, rating)
      this._setMessageActionButtonsDisabled(message, false)
    }.bind(this)).catch(function () {
      message.setAttribute('data-feedback-state', 'error')
      this._setMessageActionButtonsDisabled(message, false)
    }.bind(this))
  }

  Widget.prototype._setMessageActionButtonsDisabled = function (message, disabled) {
    if (!message || !message.querySelectorAll) return
    Array.prototype.slice.call(message.querySelectorAll('[data-wc-message-action^="feedback"]')).forEach(function (button) {
      button.disabled = disabled
    })
  }

  Widget.prototype._copyMessageResult = function (message, button, meta) {
    var text = meta && meta.copyText ? String(meta.copyText) : ''
    if (!text && message && message.querySelector) {
      var body = message.querySelector('.wc-message-body')
      text = body ? body.innerText : message.innerText
    }
    if (!text) return
    var setCopied = function () {
      button.classList.add('is-copied')
      button.setAttribute('aria-label', 'Résultat copié')
      button.setAttribute('title', 'Résultat copié')
      setTimeout(function () {
        button.classList.remove('is-copied')
        button.setAttribute('aria-label', 'Copier le résultat')
        button.setAttribute('title', 'Copier le résultat')
      }, 1400)
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(setCopied).catch(function () {})
      return
    }
    setCopied()
  }

  Widget.prototype._setMessageFeedbackRating = function (message, rating) {
    if (!message || !message.querySelectorAll) return
    Array.prototype.slice.call(message.querySelectorAll('[data-wc-message-action]')).forEach(function (button) {
      var action = button.getAttribute('data-wc-message-action')
      var selected = (rating === 'ok' && action === 'feedback-ok') || (rating === 'not_ok' && action === 'feedback-not-ok')
      button.classList.toggle('is-selected', selected)
      if (action === 'feedback-ok' || action === 'feedback-not-ok') {
        button.setAttribute('aria-pressed', selected ? 'true' : 'false')
      }
    })
  }

  Widget.prototype._getUserDisplayName = function () {
    return getUserDisplayName(this._storedUser, this.config.userName || this.opts.userName)
  }

  Widget.prototype._setDecoratedMessageBody = function (el, html) {
    if (!el) return
    if (el.getAttribute && el.getAttribute('data-wc-message-decorated') === 'true') {
      var body = el.querySelector('.wc-message-body')
      if (body) {
        body.innerHTML = html
        return
      }
    }
    el.innerHTML = html
  }

  Widget.prototype._scrollToBottom = function () {
    if (!this.messagesEl) return
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight
  }

  Widget.prototype._appendTable = function (tableUi) {
    var classes = this._cls('message') + ' ' + this._cls('bot') + ' wc-table-message'
    var el = createElement('div', { class: classes })
    this._renderTableInto(el, tableUi)
    this._decorateChatMessage(el, 'bot', { radarRunId: tableUi && tableUi.radarRunId })
    this.messagesEl.appendChild(el)
    this._scrollToBottom()
  }

  Widget.prototype._renderTableInto = function (el, tableUi) {
    if (!el) return
    this._setDecoratedMessageBody(el, renderTableUi(tableUi))
    Array.prototype.slice.call(el.querySelectorAll('.wc-table-page')).forEach(function (button) {
      button.addEventListener('click', function () {
        this._handleTablePage(button)
      }.bind(this))
    }.bind(this))
    Array.prototype.slice.call(el.querySelectorAll('[data-radar-feedback-rating]')).forEach(function (button) {
      button.addEventListener('click', function () {
        this._handleRadarFeedback(button)
      }.bind(this))
    }.bind(this))
    bindTableUi(el, tableUi, {
      onAction: function (action) {
        this._sendCapabilityAction(action, { capability: 'RADAR', profileKey: 'radar' })
      }.bind(this)
    })
  }

  Widget.prototype._appendCriteriaChips = function (criteriaUi) {
    var classes = this._cls('message') + ' ' + this._cls('bot') + ' wc-criteria-message'
    var el = createElement('div', { class: classes })
    el.innerHTML = renderCriteriaChipsUi(criteriaUi)
    bindCriteriaChipsUi(el, criteriaUi, {
      onSelect: function (chip) {
        this._handleClarificationOption({
          label: chip.label,
          action: chip.action
        })
      }.bind(this)
    })
    Array.prototype.slice.call(el.querySelectorAll('[data-radar-feedback-rating]')).forEach(function (button) {
      button.addEventListener('click', function () {
        this._handleRadarFeedback(button)
      }.bind(this))
    }.bind(this))
    this._decorateChatMessage(el, 'bot', { radarRunId: criteriaUi && criteriaUi.radarRunId })
    this.messagesEl.appendChild(el)
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight
  }

  Widget.prototype._appendClarificationOptions = function (clarificationUi) {
    var classes = this._cls('message') + ' ' + this._cls('bot') + ' wc-clarification-message'
    var el = createElement('div', { class: classes })
    el.innerHTML = renderClarificationOptionsUi(clarificationUi)
    bindClarificationOptionsUi(el, clarificationUi, {
      onSelect: function (option) {
        this._handleClarificationOption(option)
      }.bind(this)
    })
    this._decorateChatMessage(el, 'bot')
    this.messagesEl.appendChild(el)
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight
  }

  Widget.prototype._appendDebriefMatterIngestion = function (debriefMatterIngestionUi) {
    var classes = this._cls('message') + ' ' + this._cls('bot') + ' wc-debrief-ingestion-message'
    var el = createElement('div', { class: classes })
    el.innerHTML = renderDebriefMatterIngestionUi(debriefMatterIngestionUi)
    bindDebriefMatterIngestionUi(el, debriefMatterIngestionUi, {
      onAction: function (option) {
        this._handleDebriefMatterIngestionOption(option)
      }.bind(this),
      onAudioFile: function () {
        this._openDebriefAudioPopin()
      }.bind(this),
      onTextPrompt: function (option) {
        this._showDebriefTextPrompt(option)
      }.bind(this),
      onSubmitMatter: function (payload) {
        this._submitDebriefMatter(payload)
      }.bind(this),
      speechLang: this.speechLang
    })
    this._decorateChatMessage(el, 'bot')
    this.messagesEl.appendChild(el)
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight
  }

  Widget.prototype._openDebriefAudioPopin = function () {
    if (this._debriefAudioPopinEl && this._debriefAudioPopinEl.parentNode) {
      this._debriefAudioPopinEl.parentNode.removeChild(this._debriefAudioPopinEl)
    }
    var host = createElement('div', { class: 'wc-debrief-audio-popin-host' })
    host.innerHTML = renderDebriefAudioPopin()
    var close = function () {
      if (host.parentNode) host.parentNode.removeChild(host)
      if (this._debriefAudioPopinEl === host) this._debriefAudioPopinEl = null
    }.bind(this)
    bindDebriefAudioPopin(host, { onClose: close })
    this.panel.appendChild(host)
    this._debriefAudioPopinEl = host
  }

  Widget.prototype._showDebriefTextPrompt = function (option) {
    var message = option && option.action && option.action.message
      ? option.action.message
      : 'Colle ici tes notes, ta transcription ou ton compte rendu brut du rendez-vous.'
    this._hideTyping()
    this._hideTransientStatus()
    this._append(message, 'bot', { copyText: message })
    if (this.input) this.input.focus()
  }

  Widget.prototype._submitDebriefMatter = function (payload) {
    var text = payload && payload.text ? String(payload.text).trim() : ''
    var audioFile = payload && payload.audioFile && typeof payload.audioFile === 'object' ? payload.audioFile : null
    if (!text && !audioFile) return
    if (this._isTokenQuotaBlocked()) {
      this._applyTokenQuotaState(this._tokenQuotaState)
      return
    }
    var method = payload && payload.method === 'audio' ? 'audio' : payload && payload.method === 'voice' ? 'voice' : 'paste'
    var sourceKind = payload && payload.sourceKind ? String(payload.sourceKind) : (method === 'voice' ? 'dictation' : 'notes')
    var userLabel = method === 'audio'
      ? 'Fichier vocal transmis'
      : method === 'voice'
        ? 'Dictée vocale transmise'
      : 'Notes transmises'
    this._append(userLabel, 'user')
    this._showTyping()

    if (!this.apiUrl) {
      this._hideTyping()
      this._append('Aucune API configurée.', 'bot')
      return
    }

    var sources = []
    if (text) {
      sources.push({
        kind: sourceKind,
        content: text,
        processingStatus: 'processable',
        confidence: 'confirmed'
      })
    }
    if (audioFile) {
      sources.push({
        kind: 'audio',
        label: audioFile.name || 'Fichier vocal',
        mediaType: audioFile.type || 'audio/*',
        size: audioFile.size || null,
        processingStatus: 'pending',
        transcriptionStatus: 'transcription_pending',
        confidence: 'confirmed'
      })
    }

    var requestContext = Object.assign({}, this.requestContext || {}, {
      debriefMatterIngestion: {
        method: method
      },
      debriefMatter: {
        sources: sources
      }
    })
    var requestText = method === 'voice'
      ? '@Debrief Générer le débrief depuis la dictée'
      : method === 'audio'
        ? '@Debrief Ajouter le fichier vocal au débrief'
      : '@Debrief Générer le débrief depuis les notes'
    var payloadToSend = this._buildChatPayloadWithContext(requestText, requestContext)
    this._sendStandardChat(payloadToSend, userLabel)
  }

  Widget.prototype._appendCapabilityBlockView = function (capabilityBlockViewUi) {
    var classes = this._cls('message') + ' ' + this._cls('bot') + ' wc-capability-block-message'
    var capability = String(capabilityBlockViewUi && capabilityBlockViewUi.capability || '').toUpperCase()
    if (capability === 'BRIEFING') {
      classes += ' wc-capability-briefing-message'
    }
    else if (capability === 'SUMMARIZE') {
      classes += ' wc-capability-summary-message'
    }
    else if (capability === 'DEBRIEF') {
      classes += ' wc-capability-debrief-message'
    }
    var el = createElement('div', { class: classes })
    this._renderCapabilityBlockViewInto(el, capabilityBlockViewUi)
    this._decorateChatMessage(el, 'bot')
    this.messagesEl.appendChild(el)
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight
  }

  Widget.prototype._replaceCapabilityBlockView = function (el, capabilityBlockViewUi) {
    if (!el || !capabilityBlockViewUi) return false
    this._renderCapabilityBlockViewInto(el, capabilityBlockViewUi)
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight
    return true
  }

  Widget.prototype._renderCapabilityBlockViewInto = function (el, capabilityBlockViewUi) {
    this._setDecoratedMessageBody(el, renderCapabilityBlockViewUi(capabilityBlockViewUi, this.translations.capabilityBlockView))
    bindCapabilityBlockViewUi(el, capabilityBlockViewUi, {
      onAction: function (action) {
        this._sendCapabilityAction(action, capabilityBlockViewUi)
      }.bind(this),
      onActivityConfirm: function (payload) {
        this._confirmDictationActivityDraft(payload)
      }.bind(this),
      onRefresh: function (ui, button) {
        var capability = String(ui && ui.capability || '').toUpperCase()
        if (capability === 'DEBRIEF') this._refreshDebriefBlockView(ui, el, button)
        else this._refreshBriefingBlockView(ui, el, button)
      }.bind(this)
    })
  }

  Widget.prototype._refreshBriefingBlockView = function (capabilityBlockViewUi, el, button) {
    if (!capabilityBlockViewUi || String(capabilityBlockViewUi.capability || '').toUpperCase() !== 'BRIEFING') return
    if (button) button.disabled = true
    this._showTransientStatus(this._resolveTransientStatusText({ code: 'BRIEFING', label: 'Recalculer le briefing' }))
    this._showTyping()

    var requestContext = Object.assign({}, this.requestContext || {}, {
      briefingRefresh: true,
      briefingCacheAction: 'refresh',
      briefingRefreshTarget: capabilityBlockViewUi.cache && capabilityBlockViewUi.cache.targetId || null
    })
    requestContext.capabilitySuggestion = Object.assign({}, requestContext.capabilitySuggestion || {}, {
      originCapability: 'BRIEFING',
      targetCapability: 'BRIEFING',
      profileKey: capabilityBlockViewUi.profileKey || null,
      label: 'Recalculer le briefing',
      reason: 'Rafraîchissement demandé depuis le briefing affiché'
    })
    var payload = this._buildChatPayloadWithContext('@Briefing Recalculer le briefing', requestContext)
    var options = {
      replaceCapabilityElement: el,
      shouldApply: function () {
        return !el || !el.parentNode || String(capabilityBlockViewUi.capability || '').toUpperCase() === 'BRIEFING'
      }
    }

    this._sendRequest('/gateway', payload).then(function (j) {
      this._hideTyping()
      this._hideTransientStatus()
      if (typeof options.shouldApply === 'function' && !options.shouldApply()) return
      this._handleGatewayResponse(j, 'Recalculer le briefing', options)
      if (button && button.parentNode) button.disabled = false
      if (this.input) this.input.focus()
    }.bind(this)).catch(function (err) {
      this._hideTyping()
      this._hideTransientStatus()
      if (button && button.parentNode) button.disabled = false
      this._append('Erreur réseau: ' + (err && err.message), 'bot')
      this._applyMissionState(null)
    })
  }

  Widget.prototype._refreshDebriefBlockView = function (capabilityBlockViewUi, el, button) {
    if (!capabilityBlockViewUi || String(capabilityBlockViewUi.capability || '').toUpperCase() !== 'DEBRIEF') return
    if (button) button.disabled = true
    this._showTransientStatus(this._resolveTransientStatusText({ code: 'DEBRIEF', label: 'Rafraîchir le bilan' }))
    this._showTyping()

    var requestContext = Object.assign({}, this.requestContext || {}, {
      debriefRefresh: true,
      debriefCacheAction: 'refresh',
      debriefRefreshTarget: capabilityBlockViewUi.cache && capabilityBlockViewUi.cache.targetId || null
    })
    requestContext.capabilitySuggestion = Object.assign({}, requestContext.capabilitySuggestion || {}, {
      originCapability: 'DEBRIEF',
      targetCapability: 'DEBRIEF',
      label: 'Rafraîchir le bilan',
      reason: 'Rafraîchissement demandé depuis le bilan Debrief affiché'
    })
    var payload = this._buildChatPayloadWithContext('@Debrief Rafraîchir le bilan', requestContext)
    var options = {
      replaceCapabilityElement: el,
      shouldApply: function () {
        return !el || !el.parentNode || String(capabilityBlockViewUi.capability || '').toUpperCase() === 'DEBRIEF'
      }
    }

    this._sendRequest('/gateway', payload).then(function (j) {
      this._hideTyping()
      this._hideTransientStatus()
      if (typeof options.shouldApply === 'function' && !options.shouldApply()) return
      this._handleGatewayResponse(j, 'Rafraîchir le bilan', options)
      if (button && button.parentNode) button.disabled = false
      if (this.input) this.input.focus()
    }.bind(this)).catch(function (err) {
      this._hideTyping()
      this._hideTransientStatus()
      if (button && button.parentNode) button.disabled = false
      this._append('Erreur réseau: ' + (err && err.message), 'bot')
      this._applyMissionState(null)
    }.bind(this))
  }

  Widget.prototype._sendCapabilityAction = function (action, capabilityBlockViewUi) {
    var label = action && action.label ? String(action.label).trim() : ''
    if (!label) return
    this._append(label, 'user')
    this._showTyping()

    var actionRequestContext = action && action.requestContext && typeof action.requestContext === 'object'
      ? action.requestContext
      : {}
    if (actionRequestContext.stepKey) {
      var stepRequestContext = Object.assign({}, actionRequestContext)
      var stepKey = String(stepRequestContext.stepKey || '').trim()
      delete stepRequestContext.stepKey
      this._gotoStep(stepKey, label, stepRequestContext)
      return
    }
    var actionSuggestion = actionRequestContext.capabilitySuggestion && typeof actionRequestContext.capabilitySuggestion === 'object'
      ? actionRequestContext.capabilitySuggestion
      : {}
    var requestContext = Object.assign({}, this.requestContext || {}, actionRequestContext, {
      capabilitySuggestion: {
        ...actionSuggestion,
        originCapability: actionSuggestion.originCapability || (capabilityBlockViewUi && capabilityBlockViewUi.capability) || null,
        originStep: actionSuggestion.originStep || null,
        profileKey: capabilityBlockViewUi && capabilityBlockViewUi.profileKey || null,
        label: label,
        reason: action.reason || '',
        displayLabel: action.displayLabel || '',
        icon: action.icon || '',
        targetCapability: action.targetCapability || 'NONE'
      }
    })
    var payload = this._buildChatPayloadWithContext(label, requestContext)

    if (this.stream) {
      this._sendStreamingChat(payload, label)
      return
    }

    this._sendStandardChat(payload, label)
  }

  Widget.prototype._appendEmailBrief = function (emailBriefUi, options) {
    var classes = this._cls('message') + ' ' + this._cls('bot') + ' wc-email-brief-message'
    var el = createElement('div', { class: classes })
    el.innerHTML = renderEmailBriefUi(emailBriefUi, options)
    if (options && options.readOnly === true) {
      this._decorateChatMessage(el, 'bot')
      this.messagesEl.appendChild(el)
      this.messagesEl.scrollTop = this.messagesEl.scrollHeight
      return
    }
    bindEmailBriefUi(el, emailBriefUi, {
      onConfirm: function (payload) {
        this._confirmEmailBrief(payload)
      }.bind(this)
    })
    this._decorateChatMessage(el, 'bot')
    this.messagesEl.appendChild(el)
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight
  }

  Widget.prototype._appendEmailSender = function (emailSenderUi) {
    var classes = this._cls('message') + ' ' + this._cls('bot') + ' wc-email-sender-message'
    var el = createElement('div', { class: classes })
    el.innerHTML = renderEmailSenderUi(emailSenderUi)
    bindEmailSenderUi(el, emailSenderUi, {
      onConfirm: function (payload) {
        this._confirmEmailSender(payload)
      }.bind(this)
    })
    this._decorateChatMessage(el, 'bot')
    this.messagesEl.appendChild(el)
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight
  }

  Widget.prototype._appendEmailGenerate = function (emailGenerateUi) {
    var classes = this._cls('message') + ' ' + this._cls('bot') + ' wc-email-generate-message'
    var el = createElement('div', { class: classes })
    var emailGenerateTranslations = this.translations && this.translations.writeEmail && this.translations.writeEmail.generate
      ? this.translations.writeEmail.generate.ui
      : null
    el.innerHTML = renderEmailGenerateUi(emailGenerateUi, emailGenerateTranslations)
    bindEmailGenerateUi(el, emailGenerateUi, {
      onCampaignTest: function (action) {
        this._openEmailCampaignTest(action)
      }.bind(this),
      onTransition: function (transition) {
        this._gotoStep(transition.target, transition.label || transition.target, transition.requestContext || {})
      }.bind(this)
    })
    this._decorateChatMessage(el, 'bot')
    this.messagesEl.appendChild(el)
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight
  }

  Widget.prototype._appendEmailAdaptiveEstimate = function (emailAdaptiveEstimateUi) {
    var classes = this._cls('message') + ' ' + this._cls('bot') + ' wc-email-generate-message'
    var el = createElement('div', { class: classes })
    el.innerHTML = renderEmailAdaptiveEstimateUi(emailAdaptiveEstimateUi)
    bindEmailAdaptiveEstimateUi(el, emailAdaptiveEstimateUi, {
      onConfirm: function (payload) {
        this._confirmEmailAdaptiveGeneration(payload)
      }.bind(this),
      onCancel: function (payload) {
        this._gotoStep(payload.target || 'EMailDefine', payload.label || 'EMailDefine', {
          emailAdaptiveGeneration: {
            action: 'cancel'
          }
        })
      }.bind(this),
      onRecipientPage: function (payload, controls) {
        this._requestEmailAdaptiveEstimateAction(payload).then(function (nextUi) {
          if (controls && typeof controls.replace === 'function') controls.replace(nextUi)
        }).catch(function (err) {
          this._append('Erreur réseau: ' + (err && err.message), 'bot')
        }.bind(this))
      }.bind(this),
      onRecipientExclude: function (payload, controls) {
        this._requestEmailAdaptiveEstimateAction(payload).then(function (nextUi) {
          if (controls && typeof controls.replace === 'function') controls.replace(nextUi)
        }).catch(function (err) {
          if (controls && typeof controls.restore === 'function') controls.restore()
          this._append('Erreur réseau: ' + (err && err.message), 'bot')
        }.bind(this))
      }.bind(this)
    })
    this._decorateChatMessage(el, 'bot')
    this.messagesEl.appendChild(el)
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight
  }

  Widget.prototype._confirmEmailAdaptiveGeneration = function (payload) {
    var label = payload && payload.label || 'Générer tous les emails'
    this._append(label, 'user')
    this._showTransientStatus(this._resolveTransientStatusText({ code: 'EMailGenerate', label: label }))
    this._showTyping()

    var requestContext = Object.assign({}, this.requestContext || {}, {
      emailAdaptiveGeneration: {
        action: 'confirm',
        estimateId: payload && payload.estimateId
      }
    })
    var body = {
      config: {
        lg: this.config.lg || this.lang
      },
      chatEntry: {
        cv: this._conversationId,
        stepKey: 'EMailGenerate',
        requestContext: requestContext
      }
    }
    var adaptiveGenerationDone = false

    this._sendStreamRequest('/gotostep/stream', body, function (eventName, data) {
      if (eventName === 'stepdone') {
        var result = data && data.result ? data.result : null
        var stepData = result && result.stepResult && result.stepResult.data ? result.stepResult.data : null
        var generated = stepData && stepData.generated ? stepData.generated : null
        if (generated && generated.generationMode === 'ai_adaptive') {
          adaptiveGenerationDone = true
          this._hideTyping()
          this._hideTransientStatus()
          this._applyMissionState(normalizeMissionState(result))
          return
        }
      }
      this._handleStreamEvent(eventName, data, label)
    }.bind(this)).then(function () {
      if (adaptiveGenerationDone) {
        this._gotoStepSilently('EMailPreview', 'Prévisualisation', {}, { quiet: false })
      }
    }.bind(this)).catch(function (err) {
      this._hideTyping()
      this._hideTransientStatus()
      this._append('Erreur réseau: ' + (err && err.message), 'bot')
      this._applyMissionState(null)
    }.bind(this))
  }

  Widget.prototype._requestEmailAdaptiveEstimateAction = function (payload) {
    var requestContext = Object.assign({}, this.requestContext || {}, {
      emailAdaptiveGeneration: payload || {}
    })
    var body = {
      config: {
        lg: this.config.lg || this.lang
      },
      chatEntry: {
        cv: this._conversationId,
        stepKey: 'EMailGenerate',
        requestContext: requestContext
      }
    }

    return this._sendRequest('/gotostep', body).then(function (response) {
      var nextUi = normalizeEmailAdaptiveEstimateUi(response)
      if (!nextUi && response && response.stepResult && response.stepResult.data) {
        nextUi = normalizeEmailAdaptiveEstimateUi(response.stepResult.data)
      }
      if (!nextUi && response && response.result) {
        nextUi = normalizeEmailAdaptiveEstimateUi(response.result)
      }
      if (!nextUi && response && response.result && response.result.stepResult && response.result.stepResult.data) {
        nextUi = normalizeEmailAdaptiveEstimateUi(response.result.stepResult.data)
      }
      if (!nextUi) {
        throw new Error('Réponse destinataires invalide')
      }
      return nextUi
    })
  }

  Widget.prototype._appendEmailPreview = function (emailPreviewUi) {
    var classes = this._cls('message') + ' ' + this._cls('bot') + ' wc-email-preview-message'
    var el = createElement('div', { class: classes })
    this._renderEmailPreviewMessage(el, emailPreviewUi)
    this._emailPreviewMessageEl = el
    this._decorateChatMessage(el, 'bot')
    this.messagesEl.appendChild(el)
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight
  }

  Widget.prototype._appendEmailCampaignTest = function (emailCampaignTestUi) {
    if (this._emailCampaignTestMessageEl && this._emailCampaignTestMessageEl.parentNode) {
      this._emailCampaignTestMessageEl.parentNode.removeChild(this._emailCampaignTestMessageEl)
    }
    var classes = 'wc-email-campaign-test-popin'
    var el = createElement('div', { class: classes })
    var renderedEmailCampaignTestUi = this._mergeEmailCampaignTestSearchState(emailCampaignTestUi)
    el.innerHTML = renderEmailCampaignTestUi(renderedEmailCampaignTestUi)
    bindEmailCampaignTestUi(el, renderedEmailCampaignTestUi, {
      onConfirm: function (payload) {
        this._confirmEmailCampaignTest(payload)
      }.bind(this),
      onProfileSearch: function (payload) {
        this._searchEmailCampaignTestProfiles(payload)
      }.bind(this),
      onClose: function () {
        if (el.parentNode) el.parentNode.removeChild(el)
        if (this._emailCampaignTestMessageEl === el) this._emailCampaignTestMessageEl = null
        if (this.input) this.input.focus()
      }.bind(this)
    })
    this._emailCampaignTestMessageEl = el
    this._copyPanelDesignTokens(el)
    var host = typeof document !== 'undefined' && document.body ? document.body : (this.panel || this.messagesEl)
    host.appendChild(el)
    if (renderedEmailCampaignTestUi && renderedEmailCampaignTestUi.profileSearchQuery && !renderedEmailCampaignTestUi.confirmed) {
      var searchInput = el.querySelector('[data-server-search-input]')
      if (searchInput && typeof searchInput.focus === 'function') {
        searchInput.focus()
        var length = String(searchInput.value || '').length
        if (typeof searchInput.setSelectionRange === 'function') searchInput.setSelectionRange(length, length)
      }
    }
  }

  Widget.prototype._mergeEmailCampaignTestSearchState = function (emailCampaignTestUi) {
    if (!emailCampaignTestUi || emailCampaignTestUi.confirmed) return emailCampaignTestUi
    var state = this._emailCampaignTestProfileSearchState
    if (!state) return emailCampaignTestUi
    if (state.batchId && emailCampaignTestUi.batchId && state.batchId !== emailCampaignTestUi.batchId) return emailCampaignTestUi
    var selectedIds = new Set((state.profileIds || []).map(function (id) { return String(id || '').trim() }).filter(Boolean))
    var profiles = Array.isArray(emailCampaignTestUi.profiles)
      ? emailCampaignTestUi.profiles.map(function (profile) {
          if (!profile || profile.kind === 'manual') return profile
          if (!selectedIds.has(profile.id)) return profile
          return Object.assign({}, profile, { selected: true })
        })
      : []
    return Object.assign({}, emailCampaignTestUi, {
      testEmail: state.testEmail || emailCampaignTestUi.testEmail,
      profileSearchQuery: state.profileSearchQuery || emailCampaignTestUi.profileSearchQuery || '',
      profiles: profiles
    })
  }

  Widget.prototype._copyPanelDesignTokens = function (targetEl) {
    if (!targetEl || !this.panel || typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') return
    var computed = window.getComputedStyle(this.panel)
    ;[
      '--ds-ink',
      '--ds-ink-soft',
      '--ds-text',
      '--ds-text-soft',
      '--ds-label',
      '--ds-muted',
      '--ds-primary',
      '--ds-primary-soft',
      '--ds-bg',
      '--ds-surface',
      '--ds-soft',
      '--ds-softest',
      '--ds-border',
      '--ds-border-strong',
      '--ds-line',
      '--ds-success',
      '--ds-success-bg',
      '--ds-action',
      '--wc-font-family'
    ].forEach(function (token) {
      var value = computed.getPropertyValue(token)
      if (value) targetEl.style.setProperty(token, value)
    })
  }

  Widget.prototype._appendEmailSend = function (emailSendUi) {
    var classes = this._cls('message') + ' ' + this._cls('bot') + ' wc-email-send-message'
    var el = createElement('div', { class: classes })
    el.innerHTML = renderEmailSendUi(emailSendUi)
    bindEmailSendUi(el, emailSendUi, {
      onClose: function () {
        this._closeSkill(emailSendUi.closeLabel || 'Fermer')
      }.bind(this)
    })
    this._decorateChatMessage(el, 'bot')
    this.messagesEl.appendChild(el)
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight
  }

  Widget.prototype._appendEmailSimpleDraft = function (emailSimpleDraftUi) {
    // Point sensible Plume simple : ce rendu est autoporteur. S'il n'est pas
    // append ici, le widget affiche les choix de step manuels à la place du
    // formulaire sujet/corps.
    var classes = this._cls('message') + ' ' + this._cls('bot') + ' wc-email-simple-draft-message'
    var el = createElement('div', { class: classes })
    el.innerHTML = renderEmailSimpleDraftUi(emailSimpleDraftUi)
    bindEmailSimpleDraftUi(el, emailSimpleDraftUi, {
      onConfirm: function (payload) {
        this._confirmEmailSimpleDraft(payload)
      }.bind(this)
    })
    this._decorateChatMessage(el, 'bot')
    this.messagesEl.appendChild(el)
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight
  }

  Widget.prototype._appendFieldSummary = function (fieldSummaryUi) {
    var classes = this._cls('message') + ' ' + this._cls('bot') + ' wc-field-summary-message'
    var el = createElement('div', { class: classes })
    el.innerHTML = renderFieldSummaryUi(fieldSummaryUi)
    bindFieldSummaryUi(el, fieldSummaryUi, {
      onAction: function (action) {
        this._sendCapabilityAction(action, { capability: 'RADAR', profileKey: 'radar' })
      }.bind(this)
    })
    this._decorateChatMessage(el, 'bot')
    this.messagesEl.appendChild(el)
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight
  }

  Widget.prototype._appendDictationActivityDraft = function (dictationActivityDraftUi) {
    var classes = this._cls('message') + ' ' + this._cls('bot') + ' wc-dictation-activity-message'
    var el = createElement('div', { class: classes })
    el.innerHTML = renderDictationActivityDraftUi(dictationActivityDraftUi)
    bindDictationActivityDraftUi(el, dictationActivityDraftUi, {
      onConfirm: function (payload) {
        this._confirmDictationActivityDraft(payload)
      }.bind(this)
    })
    this._decorateChatMessage(el, 'bot')
    this.messagesEl.appendChild(el)
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight
  }

  Widget.prototype._appendDictationWritingPlan = function (dictationWritingPlanUi, options) {
    options = options || {}
    var classes = this._cls('message') + ' ' + this._cls('bot') + ' wc-dictation-writing-plan-message'
    var el = createElement('div', { class: classes })
    el.innerHTML = renderDictationWritingPlanUi(dictationWritingPlanUi, { readOnly: options.readOnly === true })
    if (options.readOnly !== true) {
      bindDictationWritingPlanUi(el, dictationWritingPlanUi, {
        onConfirm: function (payload) {
          this._confirmDictationWritingPlan(payload)
        }.bind(this),
        onCancel: function (payload) {
          this._confirmDictationWritingPlan(payload)
        }.bind(this)
      })
    }
    this._decorateChatMessage(el, 'bot')
    this.messagesEl.appendChild(el)
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight
  }

  Widget.prototype._appendDictationWritingResult = function (dictationWritingResultUi) {
    var classes = this._cls('message') + ' ' + this._cls('bot') + ' wc-dictation-writing-result-message'
    var el = createElement('div', { class: classes })
    el.innerHTML = renderDictationWritingResultUi(dictationWritingResultUi)
    this._decorateChatMessage(el, 'bot')
    this.messagesEl.appendChild(el)
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight
  }

  Widget.prototype._appendQuoteDraft = function (quoteDraftUi) {
    var classes = this._cls('message') + ' ' + this._cls('bot') + ' wc-quote-draft-message'
    this._lastQuoteDraftUi = quoteDraftUi
    if (!this._quoteDraftDialogue.length && quoteDraftUi.message && !isQuoteDraftInitialPrompt(quoteDraftUi.message)) {
      this._quoteDraftDialogue.push({
        role: 'assistant',
        text: quoteDraftUi.message
      })
    }
    var el = this._quoteDraftPopinEl
    if (!el || !el.parentNode) {
      el = createElement('div', { class: classes })
      this._quoteDraftPopinEl = el
      this._copyPanelDesignTokens(el)
      var host = typeof document !== 'undefined' && document.body ? document.body : (this.panel || this.messagesEl)
      host.appendChild(el)
    }
    this._renderQuoteDraftPopin(el, quoteDraftUi)
  }

  Widget.prototype._renderQuoteDraftPopin = function (el, quoteDraftUi) {
    if (!el || !quoteDraftUi) return
    var renderedUi = Object.assign({}, quoteDraftUi, {
      dialogue: this._quoteDraftDialogue.slice(),
      pending: this._quoteDraftPending === true
    })
    el.innerHTML = renderQuoteDraftUi(renderedUi)
    bindQuoteDraftUi(el, renderedUi, {
      onCancel: function () {
        this._cancelQuoteDraft(el)
      }.bind(this),
      onSubmit: function (value) {
        this._sendQuoteDraftRequest(value, el)
      }.bind(this),
      onDictate: function (input, button) {
        this._toggleQuoteDraftDictation(input, button, el)
      }.bind(this),
      onSubmitQuote: function () {
        this._sendQuoteDraftRequest('Envoyer vers votre application', el, 'submit')
      }.bind(this)
    })
  }

  Widget.prototype._cancelQuoteDraft = function (popinEl) {
    if (popinEl && popinEl.parentNode) popinEl.parentNode.removeChild(popinEl)
    if (this._quoteDraftPopinEl === popinEl) this._quoteDraftPopinEl = null
    this._quoteDraftDialogue = []
    this._quoteDraftPending = false
    this._lastQuoteDraftUi = null
    this._applyMissionState(null)
    this._clearStepChoices()

    if (!this.apiUrl) return

    var payload = {
      config: {
        lg: this.config.lg || this.lang
      },
      chatEntry: {
        cv: this._conversationId
      }
    }

    this._sendRequest('/stopskill', payload).then(function (j) {
      if (j && j.conversationId) this._conversationId = j.conversationId
      this._hideTransientStatus()
      this._applyMissionState(null)
      this._clearStepChoices()
      this._append('agent annulé', 'bot')
      if (this.input) this.input.focus()
    }.bind(this)).catch(function (err) {
      this._append('Erreur réseau: ' + (err && err.message), 'bot')
    }.bind(this))
  }

  Widget.prototype._sendQuoteDraftRequest = function (value, popinEl, action) {
    var text = String(value || '').trim()
    if (!text) return
    if (this._isTokenQuotaBlocked()) {
      this._applyTokenQuotaState(this._tokenQuotaState)
      return
    }
    this._quoteDraftDialogue.push({ role: 'user', text: text })
    this._quoteDraftPending = true
    if (popinEl && this._lastQuoteDraftUi) {
      this._renderQuoteDraftPopin(popinEl, this._lastQuoteDraftUi)
    }
    if (!this.apiUrl) {
      this._quoteDraftPending = false
      this._appendQuoteDraftDialogueAssistant('Aucune API configurée.', popinEl)
      return
    }
    var requestContext = Object.assign({}, this.requestContext || {}, {
      quoteDraftInteraction: {
        source: 'quote-draft-popin',
        action: action || 'message'
      }
    })
    var payload = this._buildChatPayloadWithContext(text, requestContext)
    this._sendRequest('/gateway', payload).then(function (j) {
      this._quoteDraftPending = false
      this._handleQuoteDraftGatewayResponse(j, popinEl, text)
      this._refreshTokenQuota()
    }.bind(this)).catch(function (err) {
      this._quoteDraftPending = false
      this._appendQuoteDraftDialogueAssistant('Erreur réseau: ' + (err && err.message), popinEl)
    }.bind(this))
  }

  Widget.prototype._toggleQuoteDraftDictation = function (input, button, popinEl) {
    if (this._isTokenQuotaBlocked()) {
      this._applyTokenQuotaState(this._tokenQuotaState)
      return
    }
    if (!input || !button || typeof window === 'undefined' || (!window.SpeechRecognition && !window.webkitSpeechRecognition)) {
      return
    }
    if (!this._quoteDraftRecognition) {
      var SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
      this._quoteDraftRecognition = new SpeechRecognition()
      this._quoteDraftRecognition.lang = this.speechLang
      this._quoteDraftRecognition.interimResults = true
      this._quoteDraftRecognition.continuous = false
      this._quoteDraftRecognition.onresult = function (event) {
        var interim = ''
        var finalTranscript = ''
        for (var i = event.resultIndex; i < event.results.length; i++) {
          var result = event.results[i]
          if (result.isFinal) finalTranscript += result[0].transcript
          else interim += result[0].transcript
        }
        input.value = (this._quoteDraftLastDictation || '') + interim + finalTranscript
        if (finalTranscript) this._quoteDraftLastDictation = (this._quoteDraftLastDictation || '') + finalTranscript
      }.bind(this)
      this._quoteDraftRecognition.onend = function () {
        this._quoteDraftRecognizing = false
        button.classList.remove('wc-mic-active')
        button.setAttribute('aria-pressed', 'false')
        if (input) input.focus()
        var text = input && input.value ? input.value.trim() : ''
        if (text) {
          setTimeout(function () {
            this._sendQuoteDraftRequest(text, popinEl)
          }.bind(this), 500)
        }
        this._quoteDraftLastDictation = ''
      }.bind(this)
    }
    if (this._quoteDraftRecognizing) {
      this._quoteDraftRecognition.stop()
      return
    }
    this._quoteDraftRecognizing = true
    button.classList.add('wc-mic-active')
    button.setAttribute('aria-pressed', 'true')
    this._quoteDraftRecognition.start()
    input.focus()
  }

  Widget.prototype._handleQuoteDraftGatewayResponse = function (j, popinEl, userText) {
    this._hideTransientStatus()
    this._clearStepChoices()
    var previousConversationId = this._conversationId
    if (j && j.conversationId) this._conversationId = j.conversationId
    if (j && j.conversationId && typeof window !== 'undefined' && typeof window.CustomEvent === 'function') {
      window.dispatchEvent(new window.CustomEvent('crysalia:conversation-updated', {
        detail: {
          conversationId: j.conversationId,
          previousConversationId: previousConversationId || null
        }
      }))
    }
    this._maybeLoadConversationSidebar()
    var result = j && j.result ? j.result : null
    this._applyMissionState(normalizeMissionState(result))
    var responseUi = this._resolveResponseUi(result)
    var quoteDraftUi = responseUi.quoteDraftUi
    var customAgentResponseUi = responseUi.customAgentResponseUi
    var assistantText = resolveQuoteDraftAssistantText(result, quoteDraftUi, userText)
    if (assistantText) {
      this._quoteDraftDialogue.push({ role: 'assistant', text: assistantText })
    }
    if (quoteDraftUi) {
      this._lastQuoteDraftUi = quoteDraftUi
      this._appendQuoteDraft(quoteDraftUi)
      return
    }
    this._appendQuoteDraftDialogueAssistant(assistantText || 'Le brouillon reste ouvert.', popinEl)
  }

  Widget.prototype._appendQuoteDraftDialogueAssistant = function (text, popinEl) {
    var message = String(text || '').trim()
    if (!message) return
    this._quoteDraftDialogue.push({ role: 'assistant', text: message })
    if (popinEl && this._lastQuoteDraftUi) {
      this._renderQuoteDraftPopin(popinEl, this._lastQuoteDraftUi)
    }
  }

  function resolveQuoteDraftAssistantText(result, quoteDraftUi, userText) {
    if (quoteDraftUi && quoteDraftUi.message && !isQuoteDraftInitialPrompt(quoteDraftUi.message) && !isSameQuoteDraftText(quoteDraftUi.message, userText)) return quoteDraftUi.message
    if (quoteDraftUi && quoteDraftUi.clarificationQuestion) return quoteDraftUi.clarificationQuestion
    var stepResult = result && result.stepResult ? result.stepResult : null
    var stepData = stepResult && stepResult.data && typeof stepResult.data === 'object' ? stepResult.data : null
    var message = String(
      (stepData && (stepData.message || stepData.clarificationQuestion)) ||
      (stepResult && (stepResult.message || stepResult.clarificationQuestion)) ||
      (result && result.message) ||
      ''
    ).trim()
    return isQuoteDraftInitialPrompt(message) || isSameQuoteDraftText(message, userText) ? '' : message
  }

  function isQuoteDraftInitialPrompt(value) {
    return String(value || '').trim() === 'Décrivez-moi ce que vous souhaitez proposer à votre interlocuteur'
  }

  function isSameQuoteDraftText(left, right) {
    return String(left || '').trim() === String(right || '').trim()
  }

  Widget.prototype._renderEmailPreviewMessage = function (el, emailPreviewUi) {
    if (!el || !emailPreviewUi) return

    this._emailPreviewHandlers = {
      onDraftChange: function (payload) {
        this._persistEmailPreviewDraft(payload)
      }.bind(this),
      onValidate: function (payload) {
        this._validateEmailPreview(payload)
      }.bind(this),
      onSend: function (payload) {
        this._confirmEmailPreview(payload)
      }.bind(this),
      onCampaignTest: function (action) {
        this._openEmailCampaignTest(action)
      }.bind(this)
    }

    this._setDecoratedMessageBody(el, renderEmailPreviewUi(emailPreviewUi, this.translations))
    bindEmailPreviewUi(el, emailPreviewUi, this._emailPreviewHandlers)
  }

  Widget.prototype._confirmEmailBrief = function (emailBriefPayload) {
    if (!emailBriefPayload || !emailBriefPayload.nextStepKey) return
    this._append(emailBriefPayload.label || emailBriefPayload.nextStepKey, 'user')
    this._showTransientStatus(this._resolveTransientStatusText({
      code: emailBriefPayload.nextStepKey,
      label: emailBriefPayload.label || emailBriefPayload.nextStepKey
    }))
    this._showTyping()

    var requestContext = Object.assign({}, this.requestContext || {}, {
      emailDefinitionPatch: emailBriefPayload.definition || null,
      emailSendingProfilePatch: emailBriefPayload.sendingProfile || null
    })
    var payload = {
      config: {
        lg: this.config.lg || this.lang
      },
      chatEntry: {
        cv: this._conversationId,
        stepKey: emailBriefPayload.nextStepKey,
        requestContext: requestContext
      }
    }

    this._sendRequest('/gotostep', payload).then(function (j) {
      this._hideTyping()
      this._hideTransientStatus()
      this._handleGatewayResponse(j, emailBriefPayload.label || emailBriefPayload.nextStepKey)
      if (this.input) this.input.focus()
    }.bind(this)).catch(function (err) {
      this._hideTyping()
      this._hideTransientStatus()
      this._append('Erreur réseau: ' + (err && err.message), 'bot')
      this._applyMissionState(null)
    }.bind(this))
  }

  Widget.prototype._confirmEmailSender = function (emailSenderPayload) {
    this._gotoStep(emailSenderPayload.nextStepKey, emailSenderPayload.label || emailSenderPayload.nextStepKey, {
      emailSenderPatch: emailSenderPayload.sender || null
    })
  }

  Widget.prototype._confirmEmailSimpleDraft = function (emailSimpleDraftPayload) {
    // Point sensible d'envoi : EMailSimpleSend attend ces deux clés exactes.
    // emailSimpleDraftPatch porte les dernières modifications utilisateur ;
    // emailSimpleSendAction distingue l'envoi réel de l'abandon.
    if (!emailSimpleDraftPayload || !emailSimpleDraftPayload.nextStepKey) return
    this._gotoStep(emailSimpleDraftPayload.nextStepKey, emailSimpleDraftPayload.label || emailSimpleDraftPayload.nextStepKey, {
      emailSimpleDraftPatch: emailSimpleDraftPayload.draft || null,
      emailSimpleSendAction: emailSimpleDraftPayload.action || null
    })
  }

  Widget.prototype._confirmEmailPreview = function (emailPreviewPayload) {
    if (!emailPreviewPayload || !emailPreviewPayload.nextStepKey) return
    this._gotoStep(emailPreviewPayload.nextStepKey, emailPreviewPayload.label || emailPreviewPayload.nextStepKey, {
      emailPreviewPatch: {
        batchId: emailPreviewPayload.batchId || null,
        activeEmailId: emailPreviewPayload.activeEmailId || null,
        validatedCount: emailPreviewPayload.validatedCount || 0,
        totalCount: emailPreviewPayload.totalCount || 0,
        validatedEmails: emailPreviewPayload.validatedEmails || [],
        emails: emailPreviewPayload.emails || []
      }
    })
  }

  Widget.prototype._confirmDictationActivityDraft = function (dictationPayload) {
    if (!dictationPayload || !dictationPayload.nextStepKey) return
    this._gotoStep(dictationPayload.nextStepKey, dictationPayload.label || dictationPayload.nextStepKey, {
      dictationActivityDraftPatch: {
        activity: dictationPayload.activity || {},
        links: dictationPayload.links || {}
      },
      dictationActivityDraftLabel: dictationPayload.label || ''
    })
  }

  Widget.prototype._confirmDictationWritingPlan = function (dictationPayload) {
    if (!dictationPayload || !dictationPayload.nextStepKey) return
    this._gotoStep(dictationPayload.nextStepKey, dictationPayload.label || dictationPayload.nextStepKey, {
      dictationWritingPlanAction: dictationPayload.action || null,
      dictationWritingPlanPatch: {
        action: dictationPayload.action || null,
        operations: Array.isArray(dictationPayload.operations) ? dictationPayload.operations : [],
        allOperationIds: Array.isArray(dictationPayload.allOperationIds) ? dictationPayload.allOperationIds : [],
        selectedOperationIds: Array.isArray(dictationPayload.selectedOperationIds) ? dictationPayload.selectedOperationIds : []
      }
    })
  }

  Widget.prototype._openEmailCampaignTest = function (action) {
    if (!action || !action.batchId) return
    this._emailCampaignTestProfileSearchSeq = (this._emailCampaignTestProfileSearchSeq || 0) + 1
    this._emailCampaignTestProfileSearchState = null
    this._gotoStepSilently('EMailCampaignTest', action.label || 'Envoyer un test', {
      emailCampaignTestPatch: {
        batchId: action.batchId
      }
    })
  }

  Widget.prototype._confirmEmailCampaignTest = function (payload) {
    if (!payload) return
    this._emailCampaignTestProfileSearchSeq = (this._emailCampaignTestProfileSearchSeq || 0) + 1
    this._emailCampaignTestProfileSearchState = null
    this._gotoStepSilently('EMailCampaignTest', payload.label || 'Envoyer les tests', {
      emailCampaignTestPatch: {
        action: 'confirm',
        batchId: payload.batchId || null,
        testEmail: payload.testEmail || '',
        profileIds: payload.profileIds || [],
        manualProfiles: payload.manualProfiles || []
      }
    })
  }

  Widget.prototype._searchEmailCampaignTestProfiles = function (payload) {
    if (!payload) return
    this._emailCampaignTestProfileSearchSeq = (this._emailCampaignTestProfileSearchSeq || 0) + 1
    var searchSeq = this._emailCampaignTestProfileSearchSeq
    this._emailCampaignTestProfileSearchState = {
      batchId: payload.batchId || null,
      testEmail: payload.testEmail || '',
      profileSearchQuery: payload.profileSearchQuery || '',
      profileIds: payload.profileIds || [],
      manualProfiles: payload.manualProfiles || []
    }
    this._gotoStepSilently('EMailCampaignTest', 'Rechercher un profil', {
      emailCampaignTestPatch: {
        batchId: payload.batchId || null,
        testEmail: payload.testEmail || '',
        profileSearchQuery: payload.profileSearchQuery || '',
        profileIds: payload.profileIds || [],
        manualProfiles: payload.manualProfiles || []
      }
    }, {
      quiet: true,
      shouldApply: function () {
        return this._emailCampaignTestProfileSearchSeq === searchSeq
      }.bind(this)
    })
  }

  Widget.prototype._persistEmailPreviewDraft = function (emailPreviewPayload) {
    if (!emailPreviewPayload) return

    var requestContext = Object.assign({}, this.requestContext || {}, {
      emailPreviewPatch: {
        batchId: emailPreviewPayload.batchId || null,
        emailId: emailPreviewPayload.emailId || emailPreviewPayload.activeEmailId || null,
        activeEmailId: emailPreviewPayload.activeEmailId || null,
        subject: emailPreviewPayload.subject || '',
        body: emailPreviewPayload.body || ''
      }
    })
    var payload = {
      config: {
        lg: this.config.lg || this.lang
      },
      chatEntry: {
        cv: this._conversationId,
        stepKey: 'EMailPreview',
        requestContext: requestContext
      }
    }

    this._sendRequest('/gotostep', payload).catch(function (err) {
      this._append('Erreur réseau: ' + (err && err.message), 'bot')
      this._applyMissionState(null)
    }.bind(this))
  }

  Widget.prototype._validateEmailPreview = function (emailPreviewPayload) {
    if (!emailPreviewPayload) return

    var requestContext = Object.assign({}, this.requestContext || {}, {
      emailPreviewPatch: {
        batchId: emailPreviewPayload.batchId || null,
        emailId: emailPreviewPayload.emailId || emailPreviewPayload.activeEmailId || null,
        activeEmailId: emailPreviewPayload.activeEmailId || null,
        validated: emailPreviewPayload.validated === false ? false : true,
        subject: emailPreviewPayload.subject || '',
        body: emailPreviewPayload.body || ''
      }
    })
    var payload = {
      config: {
        lg: this.config.lg || this.lang
      },
      chatEntry: {
        cv: this._conversationId,
        stepKey: 'EMailPreview',
        requestContext: requestContext
      }
    }

    this._sendRequest('/gotostep', payload).then(function (j) {
      this._hideTyping()
      this._hideTransientStatus()
      this._refreshEmailPreviewFromResponse(j)
      if (this.input) this.input.focus()
    }.bind(this)).catch(function (err) {
      this._hideTyping()
      this._hideTransientStatus()
      this._append('Erreur réseau: ' + (err && err.message), 'bot')
      this._applyMissionState(null)
    }.bind(this))
  }

  Widget.prototype._refreshEmailPreviewFromResponse = function (response) {
    var result = response && response.result ? response.result : null
    if (result) {
      this._applyMissionState(normalizeMissionState(result))
    }
    // Résolution centralisée des UIs métier. Le brouillon simple Plume doit
    // être reconnu ici avant la gestion générique des transitions de step.
    var responseUi = this._resolveResponseUi(result)
    var emailPreviewUi = responseUi && responseUi.emailPreviewUi ? responseUi.emailPreviewUi : null
    if (!emailPreviewUi || !this._emailPreviewMessageEl) return

    this._renderEmailPreviewMessage(this._emailPreviewMessageEl, emailPreviewUi)
  }

  Widget.prototype._gotoStep = function (stepKey, fallbackLabel, requestContextPatch, options) {
    if (!stepKey) return
    this._append(fallbackLabel || stepKey, 'user')
    this._showTransientStatus(this._resolveTransientStatusText({ code: stepKey, label: fallbackLabel || stepKey }))
    this._showTyping()

    var requestContext = Object.assign({}, this.requestContext || {}, requestContextPatch || {})
    var payload = {
      config: {
        lg: this.config.lg || this.lang
      },
      chatEntry: {
        cv: this._conversationId,
        stepKey: stepKey,
        requestContext: requestContext
      }
    }

    if (options && options.stream) {
      this._sendStreamRequest('/gotostep/stream', payload, function (eventName, data) {
        this._handleStreamEvent(eventName, data, fallbackLabel || stepKey)
      }.bind(this)).catch(function (err) {
        this._hideTyping()
        this._hideTransientStatus()
        this._append('Erreur réseau: ' + (err && err.message), 'bot')
        this._applyMissionState(null)
      }.bind(this))
      return
    }

    this._sendRequest('/gotostep', payload).then(function (j) {
      this._hideTyping()
      this._hideTransientStatus()
      this._handleGatewayResponse(j, fallbackLabel || stepKey)
      if (this.input) this.input.focus()
    }.bind(this)).catch(function (err) {
      this._hideTyping()
      this._hideTransientStatus()
      this._append('Erreur réseau: ' + (err && err.message), 'bot')
      this._applyMissionState(null)
    }.bind(this))
  }

  Widget.prototype._gotoStepSilently = function (stepKey, fallbackLabel, requestContextPatch, options) {
    if (!stepKey) return
    options = options || {}
    if (!options.quiet) {
      this._showTransientStatus(this._resolveTransientStatusText({ code: stepKey, label: fallbackLabel || stepKey }))
      this._showTyping()
    }

    var requestContext = Object.assign({}, this.requestContext || {}, requestContextPatch || {})
    var payload = {
      config: {
        lg: this.config.lg || this.lang
      },
      chatEntry: {
        cv: this._conversationId,
        stepKey: stepKey,
        requestContext: requestContext
      }
    }

    this._sendRequest('/gotostep', payload).then(function (j) {
      if (!options.quiet) {
        this._hideTyping()
        this._hideTransientStatus()
      }
      if (typeof options.shouldApply === 'function' && !options.shouldApply()) return
      this._handleGatewayResponse(j, fallbackLabel || stepKey, options)
      if (!options.quiet && this.input) this.input.focus()
    }.bind(this)).catch(function (err) {
      if (!options.quiet) {
        this._hideTyping()
        this._hideTransientStatus()
      }
      this._append('Erreur réseau: ' + (err && err.message), 'bot')
      this._applyMissionState(null)
    }.bind(this))
  }

  Widget.prototype._closeSkill = function (fallbackLabel) {
    if (fallbackLabel) {
      this._append(fallbackLabel, 'user')
    }
    this._showTyping()

    var closePayload = {
      config: {
        lg: this.config.lg || this.lang
      },
      chatEntry: {
        cv: this._conversationId
      }
    }

    this._sendRequest('/closeskill', closePayload).then(function (j) {
      this._hideTyping()
      this._hideTransientStatus()
      this._handleGatewayResponse(j)
      if (this.input) this.input.focus()
    }.bind(this)).catch(function (err) {
      this._hideTyping()
      this._hideTransientStatus()
      this._append('Erreur réseau: ' + (err && err.message), 'bot')
    }.bind(this))
  }

  Widget.prototype._appendStepChoices = function (choices) {
    if (!Array.isArray(choices) || !choices.length) return
    this._clearStepChoices()
    this._setChatInputHidden(true)

    var hasRadarChoice = choices.some(function (choice) {
      return choice && (choice.key === 'QueryRefine' || choice.key === 'QueryResult')
    })
    var classes = this._cls('message') + ' ' + this._cls('bot') + ' wc-step-choices' + (hasRadarChoice ? ' wc-step-choices-radar' : '')
    var wrapper = createElement('div', { class: classes })
    var row = createElement('div', { class: 'wc-step-choices-row' })

    choices.forEach(function (choice) {
      var button = createElement('button', {
        type: 'button',
        class: 'wc-step-choice',
        'data-step-key': choice.key
      })
      button.innerHTML = renderStepChoiceIcon(choice) + '<span>' + escapeHtml(choice.label || choice.key) + '</span>'
      button.addEventListener('click', function () {
        this._handleStepChoice(choice)
      }.bind(this))
      row.appendChild(button)
    }.bind(this))

    wrapper.appendChild(row)
    this.stepChoicesEl = wrapper
    this.messagesEl.appendChild(wrapper)
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight
  }

  function renderStepChoiceIcon(choice) {
    var key = choice && choice.key ? String(choice.key) : ''
    if (key === 'QueryRefine') return renderLucideIcon('refreshCcw')
    if (key === 'QueryResult') return renderLucideIcon('listOrdered')
    return renderLucideIcon('chevronRight')
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  function startOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate())
  }

  function startOfWeek(date) {
    var start = startOfDay(date)
    var mondayOffset = (start.getDay() + 6) % 7
    start.setDate(start.getDate() - mondayOffset)
    return start
  }

  function getConversationSectionKey(conversation, now) {
    var value = conversation && (conversation.lastEventAt || conversation.updatedAt || conversation.createdAt)
    var date = value ? new Date(value) : null
    if (!date || Number.isNaN(date.getTime())) return 'older'

    var conversationDay = startOfDay(date)
    var today = startOfDay(now)
    if (conversationDay.getTime() === today.getTime()) return 'today'
    if (conversationDay >= startOfWeek(today)) return 'week'
    if (conversationDay >= new Date(today.getFullYear(), today.getMonth(), 1)) return 'month'
    return 'older'
  }

  function renderConversationSections(conversations, renderItem, titleClass) {
    var sections = [
      { key: 'today', label: "Aujourd'hui", items: [] },
      { key: 'week', label: 'Cette semaine', items: [] },
      { key: 'month', label: 'Ce mois-ci', items: [] },
      { key: 'older', label: 'Plus ancien', items: [] }
    ]
    var now = new Date()
    ;(conversations || []).forEach(function (conversation) {
      var key = getConversationSectionKey(conversation, now)
      var section = sections.find(function (item) { return item.key === key })
      if (section) section.items.push(conversation)
    })

    return sections.map(function (section) {
      if (!section.items.length) return ''
      return (
        '<div class="' + titleClass + '" role="heading" aria-level="3">' + escapeHtml(section.label) + '</div>' +
        section.items.map(renderItem).join('')
      )
    }).join('')
  }

  function findNestedRadarRunId(message) {
    if (!message || !message.querySelector) return ''
    var scope = message.querySelector('[data-radar-run-id]')
    return scope ? String(scope.getAttribute('data-radar-run-id') || '') : ''
  }

  function isTechnicalRoutingText(value) {
    return /^Routed to skill\b/i.test(String(value || '').trim())
  }

  function buildQueryString(params) {
    var pairs = []
    Object.keys(params || {}).forEach(function (key) {
      var value = params[key]
      if (value === undefined || value === null || value === '') return
      pairs.push(encodeURIComponent(key) + '=' + encodeURIComponent(String(value)))
    })
    return pairs.length ? '?' + pairs.join('&') : ''
  }

  Widget.prototype._clearStepChoices = function () {
    if (!this.stepChoicesEl || !this.stepChoicesEl.parentNode) return
    this.stepChoicesEl.parentNode.removeChild(this.stepChoicesEl)
    this.stepChoicesEl = null
    this._setChatInputHidden(false)
  }

  Widget.prototype._setChatInputHidden = function (hidden) {
    if (!this.footerEl) return
    if (hidden) {
      this.footerEl.setAttribute('hidden', 'hidden')
      this.panel && this.panel.classList.add('wc-input-hidden')
      return
    }

    this.footerEl.removeAttribute('hidden')
    this.panel && this.panel.classList.remove('wc-input-hidden')
  }

  Widget.prototype._showTyping = function () {
    if (this.transientStatusEl && this.transientStatusEl.parentNode === this.messagesEl) return
    if (this.typingIndicator) return
    this.typingIndicator = createTypingIndicator()
    this.messagesEl.appendChild(this.typingIndicator)
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight
  }

  Widget.prototype._hideTyping = function () {
    if (!this.typingIndicator) return
    if (this.typingIndicator.parentNode) {
      this.typingIndicator.parentNode.removeChild(this.typingIndicator)
    }
    this.typingIndicator = null
  }

  Widget.prototype._showTransientStatus = function (text, meta) {
    var resolvedText = String(text || '').trim()
    if (!resolvedText) return
    if (!this.transientStatusEl) {
      this.transientStatusEl = createElement('div', {
        class: 'wc-transient-status',
        'aria-live': 'polite'
      })
    }
    this._hideTyping()
    this.transientStatusEl.innerHTML = '<span class="wc-transient-status-text"></span>'
    var textEl = this.transientStatusEl.querySelector('.wc-transient-status-text')
    if (textEl) textEl.textContent = resolvedText
    if (meta && meta.kind) this.transientStatusEl.setAttribute('data-status-kind', meta.kind)
    if (this.typingIndicator && this.typingIndicator.parentNode === this.messagesEl) {
      this.messagesEl.insertBefore(this.transientStatusEl, this.typingIndicator)
    } else if (this.transientStatusEl.parentNode !== this.messagesEl) {
      this.messagesEl.appendChild(this.transientStatusEl)
    }
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight
  }

  Widget.prototype._hideTransientStatus = function () {
    if (!this.transientStatusEl) return
    if (this.transientStatusEl.parentNode) {
      this.transientStatusEl.parentNode.removeChild(this.transientStatusEl)
    }
    this.transientStatusEl = null
  }

  Widget.prototype._resolveTransientStatusText = function (data) {
    if (data && data.statusMessage) return data.statusMessage

    var stepCode = data && (data.code || data.stepKey || data.key)
    var stepLabel = data && (data.label || stepCode)
    var skill = data && data.skill ? data.skill : null
    var skillName = skill && (skill.displayName || skill.marketingName || skill.label || skill.code)
    var isFrench = (this.lang || 'fr') === 'fr'
    var stepMessages = isFrench
      ? {
          RecordSummary: { standalone: 'Je rassemble les informations', withSkill: 'rassemble les informations' },
          RecordSummaryDialogue: { standalone: 'Je traite votre demande', withSkill: 'traite votre demande' },
          QueryEntityBuild: { standalone: "J'analyse les données", withSkill: 'analyse les données' },
          QueryLaunch: { standalone: 'Je lance la recherche', withSkill: 'lance la recherche' },
          QueryRefine: { standalone: "J'affine les critères", withSkill: 'affine les critères' },
          QueryResult: { standalone: 'Je prépare les résultats', withSkill: 'prépare les résultats' },
          EMailTargetDefine: { standalone: 'Je détermine les destinataires', withSkill: 'détermine les destinataires' },
          EMailDefine: { standalone: "Je prépare le brief email", withSkill: 'prépare le brief email' },
          EMailValidateSender: { standalone: "Je vérifie l'expéditeur", withSkill: "vérifie l'expéditeur" },
          EMailGenerate: { standalone: "Je génère l'email", withSkill: "génère l'email" },
          EMailPreview: { standalone: 'Je prépare la prévisualisation', withSkill: 'prépare la prévisualisation' },
          EMailSend: { standalone: "J'envoie les emails validés", withSkill: 'envoie les emails validés' }
        }
      : {
          RecordSummary: { standalone: 'Preparing the summary', withSkill: 'is preparing the summary' },
          RecordSummaryDialogue: { standalone: 'Handling your request', withSkill: 'is handling your request' },
          QueryEntityBuild: { standalone: 'Analyzing data', withSkill: 'is analyzing data' },
          QueryLaunch: { standalone: 'Running the search', withSkill: 'is running the search' },
          QueryRefine: { standalone: 'Refining the criteria', withSkill: 'is refining the criteria' },
          QueryResult: { standalone: 'Preparing the results', withSkill: 'is preparing the results' },
          EMailTargetDefine: { standalone: 'Finding recipients', withSkill: 'is finding recipients' },
          EMailDefine: { standalone: 'Preparing the email brief', withSkill: 'is preparing the email brief' },
          EMailValidateSender: { standalone: 'Checking the sender', withSkill: 'is checking the sender' },
          EMailGenerate: { standalone: 'Generating the email', withSkill: 'is generating the email' },
          EMailPreview: { standalone: 'Preparing the preview', withSkill: 'is preparing the preview' },
          EMailSend: { standalone: 'Sending validated emails', withSkill: 'is sending validated emails' }
        }

    if (stepCode && stepMessages[stepCode]) {
      return skillName
        ? skillName + ' ' + stepMessages[stepCode].withSkill
        : stepMessages[stepCode].standalone
    }
    if (stepLabel && skillName) return isFrench
      ? 'Je travaille sur ' + stepLabel + ' avec ' + skillName
      : 'Working on ' + stepLabel + ' with ' + skillName
    if (stepLabel) return isFrench ? 'Je travaille sur ' + stepLabel : 'Working on ' + stepLabel
    return isFrench ? 'Je continue' : 'Working'
  }

  Widget.prototype._renderHistory = function () {
    var self = this
    this.messagesEl.innerHTML = ''
    ;(this.history || []).forEach(function (entry, index) {
      if (entry && entry.welcome) {
        self._appendWelcome()
        return
      }
      var text = entry.text || entry.message || ''
      var isIntro = index === 0 && entry.from !== 'user'
      self._append(text, entry.from, isIntro ? { suppressActions: true, copyText: text } : { copyText: text })
    })
  }

  Widget.prototype.loadConversation = function (conversationId, options) {
    var id = String(conversationId || '').trim()
    if (!id) return Promise.reject(new Error('conversationId requis'))
    options = options || {}
    this._hideTyping()
    this._hideTransientStatus()
    this._clearStepChoices()
    this._showTyping()

    var query = buildQueryString({
      limit: options.limit,
      cursor: options.cursor
    })
    return this._sendRequest('/gateway/conversations/' + encodeURIComponent(id) + '/reset-interaction', {}, {
      method: 'POST'
    }).then(function () {
      return this._sendRequest('/gateway/conversations/' + encodeURIComponent(id) + query, null, {
        method: 'GET'
      })
    }.bind(this)).then(function (payload) {
      this._hideTyping()
      this._renderConversationHistory(payload, options)
      this._renderConversationSidebar()
      return payload
    }.bind(this)).catch(function (err) {
      this._handleRequestError(err)
      throw err
    }.bind(this))
  }

  Widget.prototype.startNewConversation = function () {
    this._conversationId = null
    this.welcomeGreeting = pickWelcomeGreeting(this.lang)
    this._hideTyping()
    this._hideTransientStatus()
    this._clearStepChoices()
    this._applyMissionState(null)
    this._clearWelcomeAction()
    this.history = [
      { from: 'bot', welcome: true, text: this.opts.initialBotMessage || this.translations.initialBotMessage }
    ]
    this._renderHistory()
    this._renderConversationSidebar()
  }

  Widget.prototype.setRequestContext = function (requestContext, options) {
    options = options || {}
    var nextContext = normalizeObjectKeys(requestContext || {})
    var changed = stableSerialize(this.requestContext || {}) !== stableSerialize(nextContext)
    this.requestContext = nextContext
    if (changed && options.resetConversation !== false) {
      this.startNewConversation()
    }
    return changed
  }

  Widget.prototype._isConversationSidebarVisible = function () {
    if (!this.conversationSidebarEnabled || !this.panel) return false
    return this.panel.classList.contains('wc-full-page') || this.panel.classList.contains('wc-expanded')
  }

  Widget.prototype._maybeLoadConversationSidebar = function () {
    if (!this._isConversationSidebarVisible()) return
    this._loadConversationSidebar()
  }

  Widget.prototype._loadConversationSidebar = function () {
    if (this._conversationSidebarLoading || !this.conversationSidebarListEl) return Promise.resolve(null)
    this._conversationSidebarLoading = true
    this._renderConversationSidebar({ loading: true })
    var queryParams = Object.assign({ limit: 80 }, this._buildConversationSidebarFilter())
    return this._sendRequest('/gateway/conversations' + buildQueryString(queryParams), null, {
      method: 'GET'
    }).then(function (payload) {
      this._conversationSidebarItems = payload && Array.isArray(payload.items) ? payload.items : []
      this._renderConversationSidebar()
      return payload
    }.bind(this)).catch(function () {
      this._renderConversationSidebar({ error: true })
      return null
    }.bind(this)).finally(function () {
      this._conversationSidebarLoading = false
    }.bind(this))
  }

  Widget.prototype._buildConversationSidebarFilter = function () {
    var currentRecord = this.requestContext && this.requestContext.currentRecord ? this.requestContext.currentRecord : null
    if (!currentRecord || typeof currentRecord !== 'object') return {}
    var objectType = currentRecord.objectType || currentRecord.type || ''
    var objectId = currentRecord.recordId || currentRecord.id || ''
    var externalId = currentRecord.externalId || ''
    if (!objectType || (!objectId && !externalId)) return {}
    return {
      objectType: objectType,
      objectId: objectId,
      externalId: externalId,
      sourceApp: currentRecord.sourceApp || ''
    }
  }

  Widget.prototype._renderConversationSidebar = function (state) {
    if (!this.conversationSidebarListEl) return
    state = state || {}
    var newConversationButton = (
      '<button class="wc-conversation-item" type="button" data-wc-new-conversation="true">' +
        '<span class="wc-conversation-item-icon">' + renderLucideIcon('plus') + '</span>' +
        '<span class="wc-conversation-item-copy">' +
          '<span class="wc-conversation-item-title">Nouvelle conversation</span>' +
        '</span>' +
      '</button>'
    )

    if (state.loading && !this._conversationSidebarItems.length) {
      this.conversationSidebarListEl.innerHTML = newConversationButton + '<div class="wc-conversation-sidebar-state">Chargement...</div>'
      this._bindConversationSidebarActions()
      return
    }
    if (state.error) {
      this.conversationSidebarListEl.innerHTML = newConversationButton + '<div class="wc-conversation-sidebar-state">Historique indisponible.</div>'
      this._bindConversationSidebarActions()
      return
    }
    if (!this._conversationSidebarItems.length) {
      this.conversationSidebarListEl.innerHTML = newConversationButton + '<div class="wc-conversation-sidebar-state">Aucune conversation.</div>'
      this._bindConversationSidebarActions()
      return
    }

    var renderConversationItem = function (conversation) {
      var id = conversation && conversation.conversationId ? String(conversation.conversationId) : ''
      var object = conversation && conversation.primaryObject ? conversation.primaryObject : null
      var contextLabels = getConversationContextLabels(conversation)
      var objectLabel = conversation && conversation.contextLabel !== undefined
        ? String(conversation.contextLabel || '')
        : contextLabels.join(' - ')
      return (
        '<button class="wc-conversation-item' + (id === this._conversationId ? ' is-active' : '') + '" type="button" data-wc-conversation-id="' + escapeHtml(id) + '">' +
          '<span class="wc-conversation-item-icon">' + renderLucideIcon(objectLabel ? 'blocks' : 'messageCircle') + '</span>' +
          '<span class="wc-conversation-item-copy">' +
            '<span class="wc-conversation-item-title">' + escapeHtml(conversation.title || 'Conversation') + '</span>' +
            (objectLabel ? '<span class="wc-conversation-item-object">' + escapeHtml(objectLabel) + '</span>' : '') +
          '</span>' +
        '</button>'
      )
    }.bind(this)
    this.conversationSidebarListEl.innerHTML = newConversationButton + renderConversationSections(
      this._conversationSidebarItems,
      renderConversationItem,
      'wc-conversation-section-title'
    )
    this._bindConversationSidebarActions()
  }

  Widget.prototype._bindConversationSidebarActions = function () {
    if (!this.conversationSidebarListEl) return
    Array.prototype.slice.call(this.conversationSidebarListEl.querySelectorAll('[data-wc-new-conversation]')).forEach(function (button) {
      button.addEventListener('click', function () {
        this.startNewConversation()
      }.bind(this))
    }.bind(this))
    Array.prototype.slice.call(this.conversationSidebarListEl.querySelectorAll('[data-wc-conversation-id]')).forEach(function (button) {
      button.addEventListener('click', function () {
        this.loadConversation(button.getAttribute('data-wc-conversation-id')).catch(function () {})
      }.bind(this))
    }.bind(this))
  }

  Widget.prototype.getConversationId = function () {
    return this._conversationId
  }

  Widget.prototype._renderConversationHistory = function (payload, options) {
    options = options || {}
    var conversation = payload && payload.conversation ? payload.conversation : null
    var events = payload && Array.isArray(payload.events) ? payload.events : []
    if (conversation && conversation.conversationId) {
      this._conversationId = conversation.conversationId
    }
    this._applyMissionState(null)
    this._hideTransientStatus()
    this._clearStepChoices()
    this._setChatInputHidden(false)
    if (!options.append) {
      this.messagesEl.innerHTML = ''
    }
    this._renderConversationEvents(events)
    if (!events.length && !options.append) {
      this._append('Aucun historique disponible.', 'bot')
    }
  }

  Widget.prototype._renderConversationEvents = function (events) {
    if (!Array.isArray(events)) return
    var pendingMemoryUpdatedMarker = false
    events.forEach(function (event) {
      if (pendingMemoryUpdatedMarker && isAssistantConversationEvent(event)) {
        this._appendMemoryUpdatedMarker()
        pendingMemoryUpdatedMarker = false
      }
      this._renderConversationEvent(event)
      if (event && (event.role === 'user' || event.type === 'user_message')) {
        pendingMemoryUpdatedMarker = event.memoryExtractionStatus === 'processed'
      }
    }.bind(this))
  }

  Widget.prototype._renderConversationEvent = function (event) {
    if (!event || typeof event !== 'object') return
    if (event.role === 'user' || event.type === 'user_message') {
      this._append(event.text || '', 'user')
      return
    }

    var display = event.display && typeof event.display === 'object' ? event.display : null
    if (display && this._appendConversationDisplay(display, event)) {
      return
    }

    var text = event.text || (display && display.payload && display.payload.text) || ''
    if (text && !isTechnicalRoutingText(text)) {
      this._append(text, 'bot', { eventId: event.id, copyText: text })
    }
  }

  Widget.prototype._appendConversationDisplay = function (display, event) {
    var kind = String(display.kind || '').toLowerCase()
    var payload = display.payload
    if (!payload || typeof payload !== 'object') {
      return false
    }

    if (kind === 'text') {
      var text = payload.text || event.text || ''
      if (text) this._append(text, 'bot', { eventId: event.id, copyText: text })
      return Boolean(text)
    }
    if (kind === 'table') {
      this._appendTable(payload)
      return true
    }
    if (kind === 'criteria_chips') {
      this._appendCriteriaChips(payload)
      return true
    }
    if (kind === 'clarification_options') {
      this._appendClarificationOptions(payload)
      return true
    }
    if (kind === 'debrief_matter_ingestion') {
      this._appendDebriefMatterIngestion(payload)
      return true
    }
    if (kind === 'capability_block') {
      this._appendCapabilityBlockView(payload)
      return true
    }
    if (kind === 'email_brief') {
      this._appendEmailBrief(payload, { readOnly: true })
      return true
    }
    if (kind === 'email_sender') {
      this._appendEmailSender(payload)
      return true
    }
    if (kind === 'email_generate') {
      this._appendEmailGenerate(payload)
      return true
    }
    if (kind === 'email_adaptive_estimate') {
      this._appendEmailAdaptiveEstimate(payload)
      return true
    }
    if (kind === 'email_preview') {
      this._appendEmailPreview(payload)
      return true
    }
    if (kind === 'email_send') {
      this._appendEmailSend(payload)
      return true
    }
    if (kind === 'email_simple_draft') {
      this._appendEmailSimpleDraft(payload)
      return true
    }
    if (kind === 'quote_draft') {
      this._appendQuoteDraft(payload)
      return true
    }
    if (kind === 'field_summary') {
      this._appendFieldSummary(payload)
      return true
    }
    if (kind === 'dictation_writing_plan') {
      var dictationWritingPlanUi = normalizeDictationWritingPlanUi(payload) || normalizeDictationWritingPlanUi({ ui: payload })
      if (dictationWritingPlanUi) {
        this._appendDictationWritingPlan(dictationWritingPlanUi, { readOnly: true })
        return true
      }
    }

    return false
  }

  Widget.prototype._appendMemoryUpdatedMarker = function () {
    if (!this.messagesEl) return
    var marker = createElement('div', {
      class: 'wc-memory-updated',
      'aria-live': 'polite',
      'aria-label': 'Mémoire mise à jour'
    })
    marker.innerHTML = renderLucideIcon('book-open', { size: 20 }) + '<span>Mémoire mise à jour</span>'
    this.messagesEl.appendChild(marker)
    this._scrollToBottom()
  }

  Widget.prototype._hydrateAuthState = function () {
    if (typeof localStorage === 'undefined') return
    if (this.useCookieAuth) {
      this.authToken = ''
      this.refreshToken = ''
      this._storedUser = this._readStoredUser()
      return
    }
    if (!this.authToken) this.authToken = localStorage.getItem(this.accessTokenKey) || this.authToken
    if (!this.refreshToken) this.refreshToken = localStorage.getItem(this.refreshTokenKey) || this.refreshToken
    this._storedUser = this._readStoredUser()
  }

  Widget.prototype._bootstrapAuth = function () {
    if (this.ssoJwt && !this.authToken) {
      return this._exchangeSsoSession().catch(function () {
        this._clearSession()
        return null
      }.bind(this))
    }
    if (this.useCookieAuth) {
      return this.verifySession().catch(function () {
        this._clearSession()
        return null
      }.bind(this))
    }
    if (!this.authToken || !this.refreshToken) {
      return Promise.resolve(null)
    }
    var self = this
    return this.verifySession().catch(function () {
      self._clearSession()
      return null
    })
  }

  Widget.prototype._readStoredUser = function () {
    if (typeof localStorage === 'undefined') return null
    try {
      return JSON.parse(localStorage.getItem(this.userKey) || 'null')
    } catch (error) {
      return null
    }
  }

  Widget.prototype._persistSession = function (session) {
    if (this._destroyed) return session
    if (this.useCookieAuth) {
      if (typeof localStorage !== 'undefined' && session && session.user) {
        localStorage.setItem(this.userKey, JSON.stringify(session.user))
      }
      this.authToken = ''
      this.refreshToken = ''
      this._storedUser = session && session.user ? session.user : this._storedUser
      return session
    }
    if (typeof localStorage === 'undefined') return session
    if (session && session.accessToken) localStorage.setItem(this.accessTokenKey, session.accessToken)
    if (session && session.refreshToken) localStorage.setItem(this.refreshTokenKey, session.refreshToken)
    if (session && session.user) localStorage.setItem(this.userKey, JSON.stringify(session.user))
    this.authToken = session && session.accessToken ? session.accessToken : this.authToken
    this.refreshToken = session && session.refreshToken ? session.refreshToken : this.refreshToken
    this._storedUser = session && session.user ? session.user : this._storedUser
    return session
  }

  Widget.prototype._clearSession = function () {
    if (this._destroyed) return
    this._clearPersistedSession()
    this.authToken = ''
    this.refreshToken = ''
    this._storedUser = null
    this._conversationSidebarItems = []
    this._renderConversationSidebar()
  }

  Widget.prototype._clearPersistedSession = function () {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(this.accessTokenKey)
      localStorage.removeItem(this.refreshTokenKey)
      localStorage.removeItem(this.userKey)
    }
  }

  Widget.prototype._buildChatPayload = function (requestText) {
    return this._buildChatPayloadWithContext(requestText, this.requestContext)
  }

  Widget.prototype._buildChatPayloadWithContext = function (requestText, requestContext) {
    var chatEntry = {
      rq: requestText,
      cv: this._conversationId
    }
    if (requestContext && Object.keys(requestContext).length) {
      chatEntry.requestContext = requestContext
    }

    return {
      config: {
        lg: this.config.lg || this.lang
      },
      chatEntry: chatEntry
    }
  }

  Widget.prototype._authHeaders = function (token) {
    var headers = { 'Content-Type': 'application/json' }
    if (token) headers.Authorization = 'Bearer ' + token
    return headers
  }

  Widget.prototype._handleRequestError = function (err) {
    this._hideTyping()
    var tokenBalance = err && err.body && err.body.tokenBalance ? err.body.tokenBalance : null
    if (err && (err.status === 402 || tokenBalance)) {
      this._applyTokenQuotaState(tokenBalance || { blocked: true, message: err.message })
      return this._refreshTokenQuota()
    }
    this._append('Erreur réseau: ' + (err && err.message), 'bot')
    this._applyMissionState(null)
  }

  Widget.prototype._sendStandardChat = function (payload, fallbackUserText) {
    return this._sendRequest('/gateway', payload).then(function (j) {
      this._hideTyping()
      this._handleGatewayResponse(j, fallbackUserText)
      this._refreshTokenQuota()
    }.bind(this)).catch(function (err) {
      this._handleRequestError(err)
    }.bind(this))
  }

  Widget.prototype._send = function () {
    if (this._isTokenQuotaBlocked()) {
      this._applyTokenQuotaState(this._tokenQuotaState)
      return
    }
    var v = this.input.value && this.input.value.trim()
    if (!v) return
    this._hideCapabilityAutocomplete()
    this._clearWelcomeAction()
    this._append(v, 'user')
    this.input.value = ''
    this._showTyping()
    // call backend
    if (!this.apiUrl) {
      this._hideTyping()
      return this._append('Aucune API configurée.', 'bot')
    }

    var payload = this._buildChatPayload(v)

    if (this.stream) {
      this._sendStreamingChat(payload, v)
      return
    }

    this._sendStandardChat(payload, v)
  }

  Widget.prototype._sendRequest = function (path, payload, options) {
    return this._fetchJsonWithAuthRetry(path, payload, false, options || {})
  }

  Widget.prototype._fetchJsonWithAuthRetry = async function (path, payload, retried, options) {
    options = options || {}
    var baseUrl = options.baseUrl || this.apiUrl
    var method = options.method || 'POST'
    var fetchOptions = {
      method: method,
      headers: this._getRequestHeaders(),
      credentials: this.useCookieAuth ? 'include' : 'same-origin'
    }
    if (method.toUpperCase() !== 'GET' && method.toUpperCase() !== 'HEAD') {
      fetchOptions.body = JSON.stringify(payload)
    }
    var response = await fetch(baseUrl.replace(/\/?$/, '') + path, fetchOptions)

    if (response.status === 401 && !retried) {
      await this._recoverAuthSession()
      return this._fetchJsonWithAuthRetry(path, payload, true, options)
    }

    var text = await response.text()
    var body = null
    if (text) {
      try {
        body = JSON.parse(text)
      } catch (err) {
        body = { message: text }
      }
    }
    if (!response.ok) {
      var err = new Error((body && body.message) || 'HTTP ' + response.status)
      err.status = response.status
      err.body = body
      throw err
    }

    return body
  }

  Widget.prototype._handleGatewayResponse = function (j, fallbackUserText, options) {
    options = options || {}
    this._hideTransientStatus()
    this._clearStepChoices()
    this._clearRadarExitAction()
    var previousConversationId = this._conversationId
    if (j && j.conversationId) this._conversationId = j.conversationId
    if (j && j.conversationId && typeof window !== 'undefined' && typeof window.CustomEvent === 'function') {
      window.dispatchEvent(new window.CustomEvent('crysalia:conversation-updated', {
        detail: {
          conversationId: j.conversationId,
          previousConversationId: previousConversationId || null
        }
      }))
    }
    this._maybeLoadConversationSidebar()
    var result = j && j.result ? j.result : null
    var missionState = normalizeMissionState(result)
    this._applyMissionState(missionState)

    var responseUi = this._resolveResponseUi(result)
    var responseEventId = resolveResponseConversationEventId(j)
    this._activeAssistantEventId = responseEventId
    var criteriaUi = responseUi.criteriaUi
    var clarificationOptionsUi = responseUi.clarificationOptionsUi
    var debriefMatterIngestionUi = responseUi.debriefMatterIngestionUi
    var tableUi = responseUi.tableUi
    var capabilityBlockViewUi = responseUi.capabilityBlockViewUi
    var emailBriefUi = responseUi.emailBriefUi
    var emailSenderUi = responseUi.emailSenderUi
    var emailGenerateUi = responseUi.emailGenerateUi
    var emailAdaptiveEstimateUi = responseUi.emailAdaptiveEstimateUi
    var emailPreviewUi = responseUi.emailPreviewUi
    var emailCampaignTestUi = responseUi.emailCampaignTestUi
    var emailSendUi = responseUi.emailSendUi
    var emailSimpleDraftUi = responseUi.emailSimpleDraftUi
    var fieldSummaryUi = responseUi.fieldSummaryUi
    var dictationWritingPlanUi = responseUi.dictationWritingPlanUi
    var dictationWritingResultUi = responseUi.dictationWritingResultUi
    var dictationActivityDraftUi = responseUi.dictationActivityDraftUi
    var quoteDraftUi = responseUi.quoteDraftUi
    var customAgentResponseUi = responseUi.customAgentResponseUi
    if (j && j.memoryExtractionStatus === 'processed') {
      this._appendMemoryUpdatedMarker()
    }
    if (capabilityBlockViewUi) {
      if (!(options.replaceCapabilityElement && this._replaceCapabilityBlockView(options.replaceCapabilityElement, capabilityBlockViewUi))) {
        this._appendCapabilityBlockView(capabilityBlockViewUi)
      }
    }
    else if (emailBriefUi) {
      this._appendEmailBrief(emailBriefUi)
    }
    else if (emailSenderUi) {
      this._appendEmailSender(emailSenderUi)
    }
    else if (emailGenerateUi) {
      this._appendEmailGenerate(emailGenerateUi)
    }
    else if (emailAdaptiveEstimateUi) {
      this._appendEmailAdaptiveEstimate(emailAdaptiveEstimateUi)
    }
    else if (emailPreviewUi) {
      this._appendEmailPreview(emailPreviewUi)
    }
    else if (emailCampaignTestUi) {
      this._appendEmailCampaignTest(emailCampaignTestUi)
    }
    else if (emailSendUi) {
      this._appendEmailSend(emailSendUi)
    }
    else if (emailSimpleDraftUi) {
      // Le formulaire de brouillon remplace les boutons de navigation du step.
      this._appendEmailSimpleDraft(emailSimpleDraftUi)
    }
    else if (fieldSummaryUi) {
      this._appendFieldSummary(fieldSummaryUi)
    }
    else if (dictationWritingResultUi) {
      this._appendDictationWritingResult(dictationWritingResultUi)
    }
    else if (dictationWritingPlanUi) {
      this._appendDictationWritingPlan(dictationWritingPlanUi)
    }
    else if (dictationActivityDraftUi) {
      this._appendDictationActivityDraft(dictationActivityDraftUi)
    }
    else if (quoteDraftUi) {
      this._appendQuoteDraft(quoteDraftUi)
    }
    else if (tableUi) {
      this._appendTable(tableUi)
    }
    else if (criteriaUi) {
      this._appendCriteriaChips(criteriaUi)
    }
    else if (clarificationOptionsUi) {
      this._appendClarificationOptions(clarificationOptionsUi)
    }
    else if (debriefMatterIngestionUi) {
      this._appendDebriefMatterIngestion(debriefMatterIngestionUi)
    }
    else if (customAgentResponseUi) {
      this._appendCustomAgentResponse(customAgentResponseUi, responseEventId)
    }

    var nextChoices = missionState && missionState.activeStep && Array.isArray(missionState.activeStep.next)
      ? missionState.activeStep.next
      : []
    var actionableNextChoices = nextChoices.filter(function (choice) {
      return choice && choice.mode !== 'auto'
    })
    // Point sensible : toute UI autoporteuse doit être listée ici. Sinon le
    // widget affiche en plus les boutons next du step, comme "Envoyer ou abandonner".
    var hasSelfContainedActionUi = Boolean(emailBriefUi || emailSenderUi || emailGenerateUi || emailAdaptiveEstimateUi || emailPreviewUi || emailCampaignTestUi || emailSendUi || emailSimpleDraftUi || dictationWritingPlanUi || dictationWritingResultUi || dictationActivityDraftUi || quoteDraftUi || clarificationOptionsUi || debriefMatterIngestionUi || customAgentResponseUi)
    var stepResult = result && result.stepResult ? result.stepResult : null
    var stepResultData = stepResult && stepResult.data && typeof stepResult.data === 'object' ? stepResult.data : null
    var stepNeedsClarification = Boolean(stepResult && (stepResult.needsClarification || (stepResultData && stepResultData.needsClarification)))

    if (actionableNextChoices.length > 0 && !hasSelfContainedActionUi && !stepNeedsClarification) {
      this._appendStepChoices(actionableNextChoices)
    }
    else if (!capabilityBlockViewUi && !emailBriefUi && !emailSenderUi && !emailGenerateUi && !emailAdaptiveEstimateUi && !emailPreviewUi && !emailCampaignTestUi && !emailSendUi && !emailSimpleDraftUi && !fieldSummaryUi && !dictationWritingPlanUi && !dictationWritingResultUi && !dictationActivityDraftUi && !quoteDraftUi && !tableUi && !criteriaUi && !clarificationOptionsUi && !debriefMatterIngestionUi && !customAgentResponseUi) {
      var reply = ''
      if (stepResult) {
        reply = stepResult.clarificationQuestion || (stepResultData && stepResultData.clarificationQuestion) || stepResult.message || ''
      }
      else if (!result || result.mode === 'routing-chat') {
        // A routing result is an internal protocol envelope, not a chat
        // message. Never expose it when the router has no user-facing text.
        reply = resolveRoutingChatReply(result)
      }
      if (reply && !isTechnicalRoutingText(reply)) {
        if (options.replaceStreamingElement && typeof this._updateStreamingChatMessage === 'function') {
          this._updateStreamingChatMessage(options.replaceStreamingElement, reply)
          if (typeof this._finalizeStreamingChatMessage === 'function') {
            this._finalizeStreamingChatMessage(options.replaceStreamingElement, reply, { eventId: responseEventId })
          }
        } else {
          this._append(reply, 'bot', { eventId: responseEventId, copyText: reply })
        }
      }
    }
    if (missionState && missionState.activeSkill && missionState.activeSkill.code === 'SEGMENT_LIST') {
      this._appendRadarExitAction()
    }
    this._activeAssistantEventId = null
  }

  Widget.prototype._appendRadarExitAction = function () {
    if (!this.messagesEl) return
    var el = createElement('div', { class: this._cls('message') + ' ' + this._cls('bot') + ' wc-radar-exit-message' })
    el.innerHTML = renderRadarExitAction('Quitter Radar')
    bindRadarExitAction(el, {
      onExit: function () {
        this._closeSkill('Quitter Radar')
      }.bind(this)
    })
    this.messagesEl.appendChild(el)
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight
  }

  Widget.prototype._clearRadarExitAction = function () {
    if (!this.messagesEl || !this.messagesEl.querySelectorAll) return
    Array.prototype.slice.call(this.messagesEl.querySelectorAll('[data-radar-exit-action]')).forEach(function (action) {
      var message = action.closest ? action.closest('.wc-radar-exit-message') : action.parentNode
      if (message && message.parentNode) message.parentNode.removeChild(message)
    })
  }

  Widget.prototype._appendCustomAgentResponse = function (ui, eventId) {
    var el = createElement('div', { class: this._cls('message') + ' ' + this._cls('bot') + ' wc-custom-agent-response-message' })
    el.innerHTML = renderCustomAgentResponseUi(ui)
    bindCustomAgentResponseActions(el, ui, function (action) {
      this._sendCapabilityAction(action, { capability: 'CUSTOM_AGENT' })
    }.bind(this))
    this._decorateChatMessage(el, 'bot', { eventId: eventId, copyText: ui.reply })
    this.messagesEl.appendChild(el)
    this._scrollToBottom()
  }

  Widget.prototype._resolveResponseUi = function (result) {
    // Méthode de routage UI : chaque normalizer accepte le résultat complet
    // puis, en fallback, la data du step. Cela couvre les réponses immédiates,
    // les gotostep et les réponses reconstruites depuis l'historique.
    var capabilityBlockViewUi = normalizeCapabilityBlockViewUi(result)
    if (!capabilityBlockViewUi && result && result.stepResult && result.stepResult.data) {
      capabilityBlockViewUi = normalizeCapabilityBlockViewUi(result.stepResult.data)
    }
    var criteriaUi = normalizeCriteriaChipsUi(result)
    if (!criteriaUi && result && result.stepResult && result.stepResult.data) {
      criteriaUi = normalizeCriteriaChipsUi(result.stepResult.data)
    }
    var clarificationOptionsUi = normalizeClarificationOptionsUi(result)
    if (!clarificationOptionsUi && result && result.stepResult && result.stepResult.data) {
      clarificationOptionsUi = normalizeClarificationOptionsUi(result.stepResult.data)
    }
    var debriefMatterIngestionUi = normalizeDebriefMatterIngestionUi(result)
    if (!debriefMatterIngestionUi && result && result.stepResult && result.stepResult.data) {
      debriefMatterIngestionUi = normalizeDebriefMatterIngestionUi(result.stepResult.data)
    }
    var tableUi = normalizeTableUi(result)
    if (!tableUi && result && result.stepResult && result.stepResult.data) {
      tableUi = normalizeTableUi(result.stepResult.data)
    }
    var emailBriefUi = normalizeEmailBriefUi(result)
    if (!emailBriefUi && result && result.stepResult && result.stepResult.data) {
      emailBriefUi = normalizeEmailBriefUi(result.stepResult.data)
    }
    var emailSenderUi = normalizeEmailSenderUi(result)
    if (!emailSenderUi && result && result.stepResult && result.stepResult.data) {
      emailSenderUi = normalizeEmailSenderUi(result.stepResult.data)
    }
    var emailGenerateUi = normalizeEmailGenerateUi(result)
    if (!emailGenerateUi && result && result.stepResult && result.stepResult.data) {
      emailGenerateUi = normalizeEmailGenerateUi(result.stepResult.data)
    }
    var emailAdaptiveEstimateUi = normalizeEmailAdaptiveEstimateUi(result)
    if (!emailAdaptiveEstimateUi && result && result.stepResult && result.stepResult.data) {
      emailAdaptiveEstimateUi = normalizeEmailAdaptiveEstimateUi(result.stepResult.data)
    }
    var emailPreviewUi = normalizeEmailPreviewUi(result)
    if (!emailPreviewUi && result && result.stepResult && result.stepResult.data) {
      emailPreviewUi = normalizeEmailPreviewUi(result.stepResult.data)
    }
    var emailCampaignTestUi = normalizeEmailCampaignTestUi(result)
    if (!emailCampaignTestUi && result && result.stepResult && result.stepResult.data) {
      emailCampaignTestUi = normalizeEmailCampaignTestUi(result.stepResult.data)
    }
    var emailSendUi = normalizeEmailSendUi(result)
    if (!emailSendUi && result && result.stepResult && result.stepResult.data) {
      emailSendUi = normalizeEmailSendUi(result.stepResult.data)
    }
    var emailSimpleDraftUi = normalizeEmailSimpleDraftUi(result)
    if (!emailSimpleDraftUi && result && result.stepResult && result.stepResult.data) {
      // Fallback critique pour Plume simple : le backend place le contrat
      // email-simple-draft dans stepResult.data.ui.
      emailSimpleDraftUi = normalizeEmailSimpleDraftUi(result.stepResult.data)
    }
    var fieldSummaryUi = normalizeFieldSummaryUi(result)
    if (!fieldSummaryUi && result && result.stepResult && result.stepResult.data) {
      fieldSummaryUi = normalizeFieldSummaryUi(result.stepResult.data)
    }
    var dictationWritingResultUi = normalizeDictationWritingResultUi(result)
    if (!dictationWritingResultUi && result && result.stepResult && result.stepResult.data) {
      dictationWritingResultUi = normalizeDictationWritingResultUi(result.stepResult.data)
    }
    var dictationWritingPlanUi = normalizeDictationWritingPlanUi(result)
    if (!dictationWritingPlanUi && result && result.stepResult && result.stepResult.data) {
      dictationWritingPlanUi = normalizeDictationWritingPlanUi(result.stepResult.data)
    }
    var dictationActivityDraftUi = normalizeDictationActivityDraftUi(result)
    if (!dictationActivityDraftUi && result && result.stepResult && result.stepResult.data) {
      dictationActivityDraftUi = normalizeDictationActivityDraftUi(result.stepResult.data)
    }
    var quoteDraftUi = normalizeQuoteDraftUi(result)
    if (!quoteDraftUi && result && result.stepResult && result.stepResult.data) {
      quoteDraftUi = normalizeQuoteDraftUi(result.stepResult.data)
    }
    var customAgentResponseUi = normalizeCustomAgentResponseUi(result)
    if (tableUi && criteriaUi) {
      tableUi.criteriaUi = criteriaUi
    }

    return {
      criteriaUi: criteriaUi,
      clarificationOptionsUi: clarificationOptionsUi,
      debriefMatterIngestionUi: debriefMatterIngestionUi,
      emailBriefUi: emailBriefUi,
      emailSenderUi: emailSenderUi,
      emailGenerateUi: emailGenerateUi,
      emailAdaptiveEstimateUi: emailAdaptiveEstimateUi,
      emailPreviewUi: emailPreviewUi,
      emailCampaignTestUi: emailCampaignTestUi,
      emailSendUi: emailSendUi,
      emailSimpleDraftUi: emailSimpleDraftUi,
      fieldSummaryUi: fieldSummaryUi,
      dictationWritingResultUi: dictationWritingResultUi,
      dictationWritingPlanUi: dictationWritingPlanUi,
      dictationActivityDraftUi: dictationActivityDraftUi,
      quoteDraftUi: quoteDraftUi,
      customAgentResponseUi: customAgentResponseUi,
      capabilityBlockViewUi: capabilityBlockViewUi,
      tableUi: tableUi
    }
  }

  Widget.prototype._normalizeNewSkillState = function (data) {
    return normalizeNewSkillState(data)
  }

  Widget.prototype._handleStepChoice = function (choice) {
    if (!choice || !choice.key) return
    this._append(choice.label || choice.key, 'user')
    this._showTransientStatus(this._resolveTransientStatusText({ code: choice.key, label: choice.label || choice.key }))
    this._showTyping()

    var payload = {
      config: {
        lg: this.config.lg || this.lang
      },
      chatEntry: {
        cv: this._conversationId,
        stepKey: choice.key
      }
    }

    this._sendRequest('/gotostep', payload).then(function (j) {
      this._hideTyping()
      this._hideTransientStatus()
      this._handleGatewayResponse(j, choice.label || choice.key)
    }.bind(this)).catch(function (err) {
      this._hideTyping()
      this._hideTransientStatus()
      this._append('Erreur réseau: ' + (err && err.message), 'bot')
      this._applyMissionState(null)
    }.bind(this))
  }

  Widget.prototype._handleClarificationOption = function (option) {
    if (!option || !option.action) return
    var label = option.label || option.id || ''
    this._append(label, 'user')
    this._showTyping()

    if (option.action.type === 'goto_step') {
      var requestContext = Object.assign({}, this.requestContext || {}, option.action.requestContext || {})
      var payload = {
        config: {
          lg: this.config.lg || this.lang
        },
        chatEntry: {
          cv: this._conversationId,
          stepKey: option.action.stepKey,
          requestContext: requestContext
        }
      }
      this._sendRequest('/gotostep', payload).then(function (j) {
        this._hideTyping()
        this._handleGatewayResponse(j, label)
        if (this.input) this.input.focus()
      }.bind(this)).catch(function (err) {
        this._handleRequestError(err)
      }.bind(this))
      return
    }

    if (option.action.type === 'send_message') {
      var message = option.action.message || label
      var messageContext = Object.assign({}, this.requestContext || {}, option.action.requestContext || {})
      var chatPayload = this._buildChatPayloadWithContext(message, messageContext)
      this._sendStandardChat(chatPayload, label)
    }
  }

  Widget.prototype._handleDebriefMatterIngestionOption = function (option) {
    if (!option || !option.action) return
    var label = option.label || option.id || ''
    this._append(label, 'user')
    this._showTyping()

    if (option.action.type === 'goto_step') {
      var requestContext = Object.assign({}, this.requestContext || {}, option.action.requestContext || {})
      var payload = {
        config: {
          lg: this.config.lg || this.lang
        },
        chatEntry: {
          cv: this._conversationId,
          stepKey: option.action.stepKey,
          requestContext: requestContext
        }
      }
      this._sendRequest('/gotostep', payload).then(function (j) {
        this._hideTyping()
        this._handleGatewayResponse(j, label)
      }.bind(this)).catch(function (err) {
        this._handleRequestError(err)
      }.bind(this))
    }
  }

  Widget.prototype._handleTablePage = function (button) {
    if (!button || button.disabled) return
    var page = Number(button.getAttribute('data-page') || 1)
    var pageSize = Number(button.getAttribute('data-page-size') || 20)
    var stepKey = button.getAttribute('data-step-key') || 'QueryResult'
    if (!Number.isFinite(page) || page < 1) return
    var tableMessage = this._findParentTableMessage(button)
    var pageButtonScope = tableMessage || button.parentNode
    this._setTablePageButtonsDisabled(pageButtonScope, true)
    this._setTableLoading(tableMessage, true)

    var payload = {
      config: {
        lg: this.config.lg || this.lang
      },
      chatEntry: {
        cv: this._conversationId,
        stepKey: stepKey,
        requestContext: {
          pagination: {
            page: page,
            pageSize: pageSize
          }
        }
      }
    }

    this._sendRequest('/gotostep', payload).then(function (j) {
      this._clearStepChoices()
      if (j && j.conversationId) this._conversationId = j.conversationId
      var result = j && j.result ? j.result : null
      this._applyMissionState(normalizeMissionState(result))
      var tableUi = this._resolveResponseUi(result).tableUi
      if (tableMessage && tableUi) {
        this._renderTableInto(tableMessage, tableUi)
        this._setTableLoading(tableMessage, false)
      } else {
        this._setTableLoading(tableMessage, false)
        this._handleGatewayResponse(j, 'Page ' + page)
      }
    }.bind(this)).catch(function (err) {
      this._setTableLoading(tableMessage, false)
      this._setTablePageButtonsDisabled(pageButtonScope, false)
      if (tableMessage) {
        this._showTablePageError(tableMessage, 'Erreur réseau: ' + (err && err.message))
      } else {
        this._append('Erreur réseau: ' + (err && err.message), 'bot')
      }
      this._applyMissionState(null)
    }.bind(this))
  }

  Widget.prototype._handleRadarFeedback = function (button) {
    if (!button || button.disabled) return
    var rating = button.getAttribute('data-radar-feedback-rating')
    if (rating !== 'ok' && rating !== 'not_ok') return

    var scope = button.closest ? button.closest('[data-radar-run-id]') : null
    var radarRunId = scope ? scope.getAttribute('data-radar-run-id') : ''
    if (!radarRunId) return

    this._setRadarFeedbackButtonsDisabled(scope, true)
    scope.setAttribute('data-feedback-state', 'loading')

    this._sendRequest('/bo/radar-runs/' + encodeURIComponent(radarRunId) + '/feedback', {
      rating: rating
    }, {
      method: 'PATCH',
      baseUrl: this.authBaseUrl || this.apiUrl
    }).then(function (response) {
      var recordedRating = normalizeRadarFeedbackResponseRating(response) || rating
      scope.setAttribute('data-feedback-state', 'sent')
      this._setRadarFeedbackRating(scope, recordedRating)
      this._setRadarFeedbackButtonsDisabled(scope, false)
    }.bind(this)).catch(function () {
      scope.setAttribute('data-feedback-state', 'error')
      this._setRadarFeedbackButtonsDisabled(scope, false)
    }.bind(this))
  }

  Widget.prototype._setRadarFeedbackButtonsDisabled = function (scope, disabled) {
    if (!scope || !scope.querySelectorAll) return
    Array.prototype.slice.call(scope.querySelectorAll('[data-radar-feedback-rating]')).forEach(function (feedbackButton) {
      feedbackButton.disabled = disabled
    })
  }

  Widget.prototype._setRadarFeedbackRating = function (scope, rating) {
    if (!scope || !scope.querySelectorAll) return
    Array.prototype.slice.call(scope.querySelectorAll('[data-radar-feedback-rating]')).forEach(function (feedbackButton) {
      var selected = feedbackButton.getAttribute('data-radar-feedback-rating') === rating
      feedbackButton.classList.toggle('is-selected', selected)
      feedbackButton.setAttribute('aria-pressed', selected ? 'true' : 'false')
    })
  }

  function normalizeRadarFeedbackResponseRating(response) {
    var rating = response && response.run && response.run.feedback
      ? response.run.feedback.rating
      : ''
    return rating === 'ok' || rating === 'not_ok' ? rating : ''
  }

  Widget.prototype._setTableLoading = function (tableMessage, loading) {
    if (!tableMessage || !tableMessage.classList) return
    if (loading) {
      tableMessage.classList.add('wc-table-loading')
      tableMessage.setAttribute('aria-busy', 'true')
      return
    }

    tableMessage.classList.remove('wc-table-loading')
    tableMessage.removeAttribute('aria-busy')
  }

  Widget.prototype._setTablePageButtonsDisabled = function (scope, disabled) {
    if (!scope || !scope.querySelectorAll) return
    Array.prototype.slice.call(scope.querySelectorAll('.wc-table-page')).forEach(function (pageButton) {
      pageButton.disabled = disabled
    })
  }

  Widget.prototype._showTablePageError = function (tableMessage, message) {
    if (!tableMessage || !tableMessage.querySelector) return
    var existing = tableMessage.querySelector('.wc-table-page-error')
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing)
    var pagination = tableMessage.querySelector('.wc-table-pagination')
    var error = createElement('div', { class: 'wc-table-page-error' }, [message])
    if (pagination && pagination.parentNode) {
      pagination.parentNode.insertBefore(error, pagination.nextSibling)
    } else {
      tableMessage.appendChild(error)
    }
  }

  Widget.prototype._findParentTableMessage = function (el) {
    var current = el
    while (current && current !== this.messagesEl) {
      if (current.classList && current.classList.contains('wc-table-message')) return current
      current = current.parentNode
    }

    return null
  }

  Widget.prototype._applyMissionState = function (missionState) {
    this.missionState = missionState || null
  }

  Widget.prototype._bindSupportShortcut = function () {
    if (this._supportShortcutBound) return
    this._supportShortcutBound = true

    document.addEventListener('keydown', function (e) {
      if (!e || !e.ctrlKey || e.shiftKey || e.altKey || e.metaKey) return
      if ((e.key || '').toLowerCase() !== 'h') return
      e.preventDefault()
      this._openSessionHistory()
    }.bind(this))
  }

  Widget.prototype._ensureHistoryPopin = function () {
    return this.uiState.popin || renderHistoryPopin(this.uiState, { result: { events: [] } })
  }

  Widget.prototype._renderSessionHistory = function (payload) {
    renderHistoryPopin(this.uiState, payload)
  }

  Widget.prototype._openSessionHistory = function () {
    if (!this.apiUrl) return
    this._showTyping()

    this._sendRequest('/sessionhistory', {
      config: {
        lg: this.config.lg || this.lang
      },
      chatEntry: {
        cv: this._conversationId
      }
    }).then(function (j) {
      this._hideTyping()
      this._renderSessionHistory(j)
    }.bind(this)).catch(function () {
      this._hideTyping()
    }.bind(this))
  }

  Widget.prototype._getRequestHeaders = function () {
    var headers = { 'Content-Type': 'application/json' }
    if (this.authToken && !this.useCookieAuth) {
      headers.Authorization = 'Bearer ' + this.authToken
    }
    return headers
  }

  Widget.prototype.login = async function (email, password) {
    if (!this.authBaseUrl) {
      throw new Error('Auth base URL not configured')
    }
    var response = await fetch(this.authBaseUrl.replace(/\/?$/, '') + '/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email, password: password }),
      credentials: this.useCookieAuth ? 'include' : 'same-origin'
    })
    var body = await response.json()
    if (!response.ok) {
      throw new Error((body && body.message) || 'Connexion impossible')
    }
    this._persistSession({
      accessToken: body.accessToken,
      refreshToken: body.refreshToken,
      user: body.user
    })
    this._maybeLoadConversationSidebar()
    return body
  }

  Widget.prototype.logout = async function () {
    var refreshToken = this.refreshToken || (typeof localStorage !== 'undefined' ? localStorage.getItem(this.refreshTokenKey) || '' : '')
    if ((refreshToken || this.useCookieAuth) && this.authBaseUrl) {
      try {
        await fetch(this.authBaseUrl.replace(/\/?$/, '') + '/auth/logout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(refreshToken ? { refreshToken: refreshToken } : {}),
          credentials: this.useCookieAuth ? 'include' : 'same-origin'
        })
      } catch (error) {
        // ignore logout network failures, local clear still happens
      }
    }
    this._clearSession()
  }

  Widget.prototype.destroy = function () {
    this._destroyed = true
    if (this.recognition) {
      try {
        this.recognition.abort()
      } catch (error) {}
      this.recognition = null
    }
    if (this._quoteDraftRecognition) {
      try {
        this._quoteDraftRecognition.abort()
      } catch (error) {}
      this._quoteDraftRecognition = null
    }

    if (this.mode === 'floating') {
      if (this.button && this.button.parentNode) {
        this.button.parentNode.removeChild(this.button)
      }
    }

    if (this.panel && this.panel.parentNode) {
      this.panel.parentNode.removeChild(this.panel)
    }

    this.button = null
    this.panel = null
    this.messagesEl = null
    this.input = null
    this.micButton = null
    this.expandButton = null
    this.closeButton = null
    this.opened = false
    this.expanded = false
  }

  Widget.prototype.getSession = function () {
    return {
      accessToken: this.useCookieAuth ? '' : this.authToken,
      refreshToken: this.useCookieAuth ? '' : this.refreshToken,
      user: this._storedUser
    }
  }

  Widget.prototype.verifySession = async function () {
    if (!this.authToken && !this.useCookieAuth) return null
    if (!this.authBaseUrl) return null
    var response = await fetch(this.authBaseUrl.replace(/\/?$/, '') + '/auth/me', {
      method: 'GET',
      headers: this._authHeaders(this.useCookieAuth ? '' : this.authToken),
      credentials: this.useCookieAuth ? 'include' : 'same-origin'
    })
    if (!response.ok) {
      if (response.status === 401) {
        await this._recoverAuthSession()
        response = await fetch(this.authBaseUrl.replace(/\/?$/, '') + '/auth/me', {
          method: 'GET',
          headers: this._authHeaders(this.useCookieAuth ? '' : this.authToken),
          credentials: this.useCookieAuth ? 'include' : 'same-origin'
        })
      }
    }
    if (!response.ok) {
      throw new Error('Session invalide')
    }
    var body = await response.json()
    this._storedUser = body
    this._persistSession({ accessToken: this.authToken, refreshToken: this.refreshToken, user: body })
    this._maybeLoadConversationSidebar()
    return body
  }

  Widget.prototype._inferAuthBaseUrl = function (apiUrl) {
    if (!apiUrl) return ''
    return apiUrl.replace(/\/nglia\/?$/, '').replace(/\/nglia$/, '').replace(/\/?$/, '')
  }

  Widget.prototype._refreshSession = async function () {
    if (!this.authBaseUrl || (!this.refreshToken && !this.useCookieAuth)) {
      throw new Error('No refresh session available')
    }

    var response = await fetch(this.authBaseUrl.replace(/\/?$/, '') + '/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(this.refreshToken ? { refreshToken: this.refreshToken } : {}),
      credentials: this.useCookieAuth ? 'include' : 'same-origin'
    })

    var body = await response.json()
    if (!response.ok) {
      throw new Error((body && body.message) || 'Refresh failed')
    }

    this._persistSession({
      accessToken: body.accessToken,
      refreshToken: body.refreshToken || this.refreshToken,
      user: body.user || this._storedUser
    })
    return body
  }

  Widget.prototype._recoverAuthSession = async function () {
    if (this.authBaseUrl && (this.refreshToken || this.useCookieAuth)) {
      try {
        return await this._refreshSession()
      } catch (error) {
        if (!this.ssoJwt) throw error
      }
    }
    if (this.ssoJwt) {
      return this._exchangeSsoSession()
    }
    throw new Error('No refresh session available')
  }

  Widget.prototype._exchangeSsoSession = async function () {
    if (!this.authBaseUrl) {
      throw new Error('Auth base URL not configured')
    }
    if (!this.ssoJwt) {
      throw new Error('No SSO JWT available')
    }

    var response = await fetch(this.authBaseUrl.replace(/\/?$/, '') + '/auth/sso/exchange', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assertionJwt: this.ssoJwt }),
      credentials: this.useCookieAuth ? 'include' : 'same-origin'
    })

    var body = await response.json()
    if (!response.ok) {
      throw new Error((body && body.message) || 'SSO exchange failed')
    }

    this._persistSession({
      accessToken: body.accessToken,
      refreshToken: body.refreshToken,
      user: body.user
    })
    return body
  }

  function isAssistantConversationEvent(event) {
    if (!event || typeof event !== 'object') return false
    if (event.role === 'assistant') return true
    return event.type === 'assistant_message' || event.type === 'capability_result'
  }

  function resolveResponseConversationEventId(response) {
    var result = response && response.result && typeof response.result === 'object' ? response.result : null
    return normalizeString(
      response && (response.conversationEventId || response.eventId || response.assistantEventId)
    ) || normalizeString(
      result && (result.conversationEventId || result.eventId || result.assistantEventId)
    )
  }

  function normalizeString(value) {
    var normalized = String(value || '').trim()
    return normalized || ''
  }

  function stableSerialize(value) {
    if (Array.isArray(value)) return '[' + value.map(stableSerialize).join(',') + ']'
    if (value && typeof value === 'object') {
      return '{' + Object.keys(value).sort().map(function (key) {
        return JSON.stringify(key) + ':' + stableSerialize(value[key])
      }).join(',') + '}'
    }
    return JSON.stringify(value)
  }

  // public API
  var instance = null

  function init(opts) {
    opts = opts || {}
    var nextSsoJwt = opts.ssoJwt || (opts.config && opts.config.ssoJwt) || ''
    if (!instance) {
      instance = new Widget(opts)
    } else if (nextSsoJwt && nextSsoJwt !== (instance.ssoJwt || '')) {
      // A changed SSO assertion means that the host account changed. Recreate
      // the widget so the new assertion is exchanged before any old session
      // can be used.
      instance.destroy()
      instance = new Widget(opts)
    } else {
      // Embedded hosts commonly call init again when their current page/record
      // changes. Keep the singleton, but do not keep the context of the old page.
      var nextContext = opts.requestContext || (opts.config && opts.config.requestContext)
      if (nextContext && typeof nextContext === 'object') {
        instance.setRequestContext(nextContext)
      }
    }
    return instance
  }

  function open() { if (instance) instance.open() }
  function close() { if (instance) instance.close() }
  function loadConversation(conversationId, options) {
    if (!instance) return Promise.reject(new Error('ChatWidget not initialized'))
    return instance.loadConversation(conversationId, options)
  }
  function startNewConversation() {
    if (instance) instance.startNewConversation()
  }
  function setRequestContext(requestContext, options) {
    if (instance) return instance.setRequestContext(requestContext, options)
    return false
  }
  function reset() {
    if (instance) {
      instance.destroy()
    }
    instance = null
  }

  installStreamMethods(Widget)
  global.ChatWidget = {
    init: init,
    open: open,
    close: close,
    reset: reset,
    loadConversation: loadConversation,
    startNewConversation: startNewConversation,
    setRequestContext: setRequestContext
  }

})(window);
